import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ArrowDownTrayIcon, ClockIcon } from "@heroicons/react/24/solid";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import ReactGA from "react-ga4";
import Plot from "react-plotly.js";
import { Link, Navigate, useNavigate } from "react-router-dom";

import AffiliateEarnings from "./AffiliateEarnings";
import EasyAIStudioCard from "./EasyAIStudioCard";
import PlatformLicenseKeysCard from "./PlatformLicenseKeys";
import PlatformUsageBreakdown from "./PlatformUsageBreakdown";
import RouteInsights from "./RouteInsights";

import { useApiKey } from "context/ApiKeyContext";
import UserContext from "context/UserContext";

import Badge from "components/atoms/Badge";
import Button from "components/atoms/Button";
import Card from "components/atoms/Card";
import EstimatedMonthlyCost from "components/atoms/EstimatedMonthlyCost";
import Spinner from "components/atoms/Spinner";
import { convertToNumLetter } from "components/molecules/PriceTables/PriceTableNutritionAPI";
import SubscriptionCard from "components/molecules/SubscriptionCard";
import CatalogManager from "components/organisms/CatalogManager";

import { error, success } from "utils";
import {
  STUDIO_INTENTS,
  STUDIO_INTENT_NEEDS_LICENSE,
  STUDIO_SUPPORTS_SDK,
  StudioIntent,
  buildStudioDirectUrl,
  withStudioRedirect,
} from "utils/easyaiStudio";
import { NUTRITION_PLATFORM_SLUG } from "utils/products";
import { ProductDataType, UsageHistoryReportForPeriod } from "utils/types";

/**
 * Copies a license key without ever awaiting, for use on the way into a
 * window.open.
 *
 * The deprecated execCommand path is tried first because it is synchronous and
 * needs no permission, so it completes inside the click gesture. The async
 * Clipboard API is only a fallback and is deliberately not awaited: it needs a
 * focused document, and by the time it would resolve the Studio tab has taken
 * focus, so it can hang on a permission prompt rendered behind the new tab.
 */
const copyLicenseWithoutBlocking = (text: string) => {
  try {
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.top = "0";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(field);
    if (copied) return;
  } catch {
    // Fall through to the async API.
  }

  try {
    void navigator.clipboard?.writeText(text).catch(() => undefined);
  } catch {
    // Best effort; the key is still visible in the API Keys card.
  }
};

// NOTE(PRICING_REFERENCE_TABLE):
// This table is an intentional UI reference for plan cards/estimator fallback.
// For subscribed-account billing summaries and Insights forecasts, use Stripe
// subscription payload values (see getSubscriptionMonthlyFeeUsd) instead.
const TIERS = [
  {
    name: "Starter",
    slug: "nutrition-platform-starter",
    price: "$99",
    priceNum: 99,
    tokens: "1M",
    tokensMillions: 1,
    refillPrice: "$25 / 1M",
    refillPricePerM: 25,
    features: {
      easyAI: true,
      localization: true,
      modelSwitching: false,
      insights: false,
      aiInsights: false,
      affiliateEarnings: true,
      support: "Email + Community Slack",
    },
    highlight: false,
  },
  {
    name: "Growth",
    slug: "nutrition-platform-growth",
    price: "$599",
    priceNum: 599,
    tokens: "5M",
    tokensMillions: 5,
    refillPrice: "$10 / 1M",
    refillPricePerM: 10,
    features: {
      easyAI: true,
      localization: true,
      modelSwitching: true,
      insights: true,
      aiInsights: false,
      affiliateEarnings: true,
      support: "Dedicated Slack Channel",
    },
    highlight: true,
  },
  {
    name: "Pro",
    slug: "nutrition-platform-pro",
    price: "$2,999",
    priceNum: 2999,
    tokens: "100M",
    tokensMillions: 100,
    refillPrice: "$2.50 / 1M",
    refillPricePerM: 2.5,
    features: {
      easyAI: true,
      localization: true,
      modelSwitching: true,
      insights: true,
      aiInsights: true,
      affiliateEarnings: true,
      support: "Enterprise Support",
    },
    highlight: false,
  },
];

const GLOBAL_ROUTE_AVG_DAYS = 30;
const DEFAULT_ROUTE_CALCULATOR_CATEGORIES = [
  "Image Detection",
  "Text & Audio",
  "Search",
  "API Calls",
];
const DEFAULT_ROUTE_CALLS_PER_USER_PER_DAY: Record<string, number> = {
  "Image Detection": 1,
  "Text & Audio": 2,
  Search: 5,
  "API Calls": 20,
};
const INFO_ICON_CLASS = "h-4 w-4 text-slate-500";
const TIER_PILL_CLASS =
  "inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700";
type ResourceLink = {
  label: string;
  href: string;
  hint?: string;
  /** Optional second link shown inline, for related destinations. */
  secondary?: { label: string; href: string };
};

const REFERENCE_LINKS: ResourceLink[] = [
  {
    label: "Documentation",
    href: "https://passio.gitbook.io/nutrition-ai",
    hint: "Guides, concepts and route reference",
  },
  {
    label: "OpenAPI Docs",
    href: "https://api.passiolife.com/v2/products/docs",
    hint: "Live schema with a try-it console",
  },
  {
    label: "Authorizing with your API key",
    href: "https://passio.gitbook.io/nutrition-ai/rest-api/api-overview/authorization-setup",
    hint: "Token exchange and request headers",
  },
];

const SUPPORT_LINKS: ResourceLink[] = [
  {
    label: "Email support",
    href: "mailto:support@passiolife.com",
    hint: "support@passiolife.com",
  },
  {
    label: "Community Slack",
    href: "https://join.slack.com/t/passio-community/shared_invite/zt-1ge2wpifb-FHhXT61e80gOPy~tzjX3Ng",
    hint: "Join the workspace",
    secondary: {
      label: "#help",
      href: "https://passio-community.slack.com/channels/help",
    },
  },
  {
    label: "Roadmap",
    href: "https://portal.productboard.com/passio/1-nutrition-ai/tabs/1-recently-launched",
    hint: "What shipped and what is next",
  },
];

type RouteAverageRow = {
  category: string;
  totalCalls: number;
  avgBilledTokens: number;
  totalBilledTokens: number;
};

type RawRouteAverageRow = {
  category?: string;
  totalCalls?: number | string;
  avgBilledTokens?: number | string;
  totalBilledTokens?: number | string;
  total_calls?: number | string;
  avg_billed_tokens?: number | string;
  total_billed_tokens?: number | string;
};

type EasyAISSOResponse = {
  ssoEnabled?: boolean;
  ssoUrl?: string;
  loginUrl?: string;
  expiresAt?: number;
};

type DetailsTab = "account" | "easyai" | "insights" | "affiliate" | "catalog";

const detailsTabToHash = (tab: DetailsTab): string => {
  switch (tab) {
    case "easyai":
      return "easy-ai";
    case "insights":
      return "insights";
    case "affiliate":
      return "affiliate-earnings";
    case "catalog":
      return "catalog-manager";
    default:
      return "account";
  }
};

const getRequestedDetailsTab = (): DetailsTab => {
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get("tab");
  const fromHash = window.location.hash.replace(/^#/, "");
  const normalized = String(fromQuery || fromHash || "account")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

  if (normalized === "easyai" || normalized === "easy") return "easyai";
  if (normalized === "insights" || normalized === "insight") return "insights";
  if (normalized === "catalogmanager" || normalized === "catalog")
    return "catalog";
  if (
    normalized === "affiliate" ||
    normalized === "affiliateearnings" ||
    normalized === "earnings"
  ) {
    return "affiliate";
  }
  return "account";
};

const toNumber = (v: unknown): number => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const getUsageQuota = (usageInfo: any): number =>
  Math.max(0, Number(usageInfo?.unitsQuota ?? usageInfo?.unitQuota ?? 0) || 0);

// How often the token meter re-reads the live Redis counter. Only the meter
// refreshes on this interval; usage graphs and history stay on their own
// cadence because they are backed by BigQuery.
const LIVE_USAGE_POLL_MS = 7000;

const normalizeRouteCategory = (category: string): string => {
  const normalized = category.trim().toLowerCase();
  if (
    normalized === "semantic search" ||
    normalized === "search" ||
    normalized === "ai search"
  ) {
    return "Search";
  }
  if (
    normalized === "vision" ||
    normalized === "ai vision tools" ||
    normalized === "image detection"
  ) {
    return "Image Detection";
  }
  return category;
};

const normalizeRouteAverageRows = (raw: unknown): RouteAverageRow[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => row as RawRouteAverageRow)
    .map((row) => ({
      category: normalizeRouteCategory(row.category ?? ""),
      totalCalls: toNumber(row.totalCalls ?? row.total_calls),
      avgBilledTokens: toNumber(row.avgBilledTokens ?? row.avg_billed_tokens),
      totalBilledTokens: toNumber(
        row.totalBilledTokens ?? row.total_billed_tokens
      ),
    }))
    .filter((row) => row.category !== "" && row.totalCalls > 0);
};

const formatTokenCount = (tokens: number): string => {
  if (!Number.isFinite(tokens) || tokens <= 0) return "0";
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}K`;
  }
  return Math.round(tokens).toLocaleString();
};

const formatUsd = (amount: number): string =>
  amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const getSubscriptionMonthlyFeeUsd = (stripeSubscription: any): number => {
  if (!stripeSubscription) return 0;

  const items = stripeSubscription?.items?.data;
  if (Array.isArray(items) && items.length > 0) {
    let monthlyTotal = 0;

    items.forEach((item) => {
      const price = item?.price || {};
      const recurring = price?.recurring || {};
      const interval = String(recurring?.interval || "").toLowerCase();
      const intervalCount = Math.max(1, Number(recurring?.interval_count || 1));
      const quantity = Math.max(1, Number(item?.quantity || 1));

      const unitAmountCents = Number(price?.unit_amount || 0);
      const unitAmountDecimalCents = Number(price?.unit_amount_decimal || 0);
      const amountUsd =
        unitAmountCents > 0
          ? unitAmountCents / 100
          : unitAmountDecimalCents > 0
          ? unitAmountDecimalCents / 100
          : 0;
      if (amountUsd <= 0) return;

      const lineAmount = amountUsd * quantity;
      if (interval === "month") {
        monthlyTotal += lineAmount / intervalCount;
      } else if (interval === "year") {
        monthlyTotal += lineAmount / (12 * intervalCount);
      }
    });

    if (monthlyTotal > 0) return monthlyTotal;
  }

  const planAmount = Number(stripeSubscription?.plan?.amount || 0);
  if (planAmount > 0) return planAmount / 100;

  return 0;
};

const formatTierDisplayName = (tierRaw?: string): string => {
  const normalized = String(tierRaw || "")
    .trim()
    .toLowerCase()
    .replace("nutrition-platform-", "");
  if (!normalized) return "Unknown";
  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const FALLBACK_PERIOD_SECONDS = 30 * 24 * 60 * 60;

const getRecurringPeriodSeconds = (stripeSubscription: any): number => {
  const recurring =
    stripeSubscription?.items?.data?.[0]?.price?.recurring ||
    stripeSubscription?.plan?.recurring ||
    {};
  const interval = String(recurring?.interval || "").toLowerCase();
  const intervalCount = Math.max(1, Number(recurring?.interval_count || 1));
  switch (interval) {
    case "day":
      return 24 * 60 * 60 * intervalCount;
    case "week":
      return 7 * 24 * 60 * 60 * intervalCount;
    case "year":
      return 365 * 24 * 60 * 60 * intervalCount;
    case "month":
      return 30 * 24 * 60 * 60 * intervalCount;
    default:
      return FALLBACK_PERIOD_SECONDS;
  }
};

const toInputDate = (unixSeconds: number): string =>
  new Date(unixSeconds * 1000).toISOString().split("T")[0];

const buildBillingPeriods = (
  stripeSubscription: any,
  maxPeriods = 24
): { start: number; end: number }[] => {
  if (!stripeSubscription) return [];

  let periodStart = Number(stripeSubscription.current_period_start || 0);
  let periodEnd = Number(stripeSubscription.current_period_end || 0);
  const subscriptionStart = Number(stripeSubscription.start_date || 0);

  if (!periodStart && !periodEnd) return [];

  if (!periodEnd && periodStart) {
    periodEnd = periodStart + getRecurringPeriodSeconds(stripeSubscription);
  }
  if (!periodStart && periodEnd) {
    periodStart = periodEnd - getRecurringPeriodSeconds(stripeSubscription);
  }

  if (periodStart > periodEnd) {
    const temp = periodStart;
    periodStart = periodEnd;
    periodEnd = temp;
  }

  let periodLength = periodEnd - periodStart;
  if (periodLength < 24 * 60 * 60) {
    periodLength = getRecurringPeriodSeconds(stripeSubscription);
    periodStart = periodEnd - periodLength;
  }

  if (periodLength < 24 * 60 * 60) {
    periodLength = FALLBACK_PERIOD_SECONDS;
    periodStart = periodEnd - periodLength;
  }

  const floorStart =
    subscriptionStart > 0
      ? subscriptionStart
      : periodStart - periodLength * maxPeriods;

  const periods: { start: number; end: number }[] = [];
  for (let i = 0; i < maxPeriods; i++) {
    const end = periodEnd - i * periodLength;
    const start = periodStart - i * periodLength;
    if (end <= floorStart) break;
    periods.push({
      start: Math.max(start, floorStart),
      end,
    });
  }

  return periods;
};

const ResourceLinkList = ({
  title,
  links,
}: {
  title: string;
  links: ResourceLink[];
}) => (
  <div className="mt-5 first:mt-4">
    <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
      {title}
    </p>
    <ul
      role="list"
      className="mt-2 divide-y divide-gray-200 rounded-md border border-gray-200"
    >
      {links.map(({ label, href, hint, secondary }) => (
        <li key={label} className="py-2.5 px-3 text-sm">
          <div className="flex items-baseline gap-2">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {label}
            </a>
            {secondary && (
              <>
                <span className="text-gray-300">·</span>
                <a
                  href={secondary.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  {secondary.label}
                </a>
              </>
            )}
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 flex-shrink-0 self-center text-gray-400" />
          </div>
          {hint && <p className="mt-0.5 text-sm text-gray-500">{hint}</p>}
        </li>
      ))}
    </ul>
  </div>
);

const HeaderHelp = ({ text }: { text: string }) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: -9999, left: -9999 });

  const tooltipCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !triggerRef.current) return;
      const r = triggerRef.current.getBoundingClientRect();
      const t = node.getBoundingClientRect();
      const gap = 8;
      const pad = 8;
      let top = r.bottom + gap;
      let left = r.left + r.width / 2 - t.width / 2;
      if (left < pad) left = pad;
      if (left + t.width > window.innerWidth - pad)
        left = window.innerWidth - pad - t.width;
      if (top + t.height > window.innerHeight - pad)
        top = r.top - t.height - gap;
      setCoords({ top, left });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible]
  );

  return (
    <span
      ref={triggerRef}
      className="ml-1 inline-flex cursor-help align-middle"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <InformationCircleIcon className={INFO_ICON_CLASS} />
      {visible &&
        createPortal(
          <div
            ref={tooltipCallbackRef}
            role="tooltip"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              zIndex: 99999,
              pointerEvents: "none",
            }}
            className="w-72 max-w-[90vw] rounded-md bg-gray-900 px-3 py-2.5 text-left text-sm font-normal normal-case leading-relaxed text-white shadow-lg"
          >
            {text}
          </div>,
          document.body
        )}
    </span>
  );
};

const DetailsNutritionPlatform = () => {
  const flags = useFlags();
  const { setApiKey } = useApiKey();

  const [unauthorized, setUnauthorized] = useState(false);
  const [productState, setProductState] = useState<ProductDataType>({
    isSubscribed: false,
    trialEnd: null,
    billingDate: new Date(),
    licenseKey: null,
    usageInfo: null,
    tiers: [],
  });

  useEffect(() => {
    if (productState?.licenseKey) {
      setApiKey(productState.licenseKey);
    }
  }, [productState?.licenseKey, setApiKey]);

  const [productSubDetail, setProductSubDetail] = useState<any>(null);
  const [autoRefillOnRenewal, setAutoRefillOnRenewal] = useState(0);
  const [refillBatchQuantity, setRefillBatchQuantity] = useState(1);
  const [refillBehaviour, setRefillBehaviour] = useState<"auto" | "off">(
    "auto"
  );
  const [refillSettingsSaving, setRefillSettingsSaving] = useState(false);
  const [refillNowLoading, setRefillNowLoading] = useState(false);
  // Zero Data Retention (ZDR) mode: when enabled, every AI request from this
  // account is restricted to models routed through Vertex AI under our
  // zero-retention / no-training contract.  Mirrored from the detail
  // response (`productSubDetail.zeroDataRetention`).
  const [zdrEnabled, setZdrEnabled] = useState<boolean>(false);
  const [zdrSaving, setZdrSaving] = useState<boolean>(false);
  const [zdrConfirmAction, setZdrConfirmAction] = useState<
    null | "enable" | "disable"
  >(null);
  // Data-region preference for ZDR traffic. "fastest" (default) applies no
  // region constraint (lowest-throttle endpoints, may leave the US); "us"
  // pins processing to US regions for residency (can be slower / throttled).
  // Only meaningful when ZDR is on. Loaded from / saved to the same
  // `/customer/zero-data-retention` endpoint as the toggle.
  const [zdrRegionMode, setZdrRegionMode] = useState<"fastest" | "us">(
    "fastest"
  );
  const [zdrRegionSaving, setZdrRegionSaving] = useState<boolean>(false);
  // Display kill-switch for the Privacy & Compliance card.  Driven by the
  // LaunchDarkly flag `zero-data-retention-controls`.  Default-on: if the
  // flag has not been created in LD yet, or LD is still loading, we SHOW
  // the controls.  Set the flag to `false` in LaunchDarkly to hide the
  // card, confirm dialogs, and the RouteInsights dropdown enforcement.
  const zdrFeatureFlagEnabled = flags["zero-data-retention-controls"] !== false;
  // Launch gate for the toggle itself.  Driven by the LaunchDarkly flag
  // `zero-data-retention-toggle-live`.  Default-OFF: until this flag is
  // explicitly created and set to `true` in LaunchDarkly, the card still
  // renders (so customers see the value prop) but the toggle is locked and
  // labelled "Coming soon" so no one can actually enable ZDR before launch.
  // Set `zero-data-retention-toggle-live = true` to go live.
  const zdrToggleLive = flags["zero-data-retention-toggle-live"] === true;
  const zdrComingSoon = !zdrToggleLive;
  // Independent launch gate for the data-region selector (the "United States
  // only" residency option), driven by `zero-data-retention-region-controls`.
  // Default-OFF: until this flag is created and set to `true` in LaunchDarkly
  // the region area still renders (so customers see it's coming) but the radios
  // are locked behind a "coming soon" note. The backend mirrors this exactly —
  // while the flag is off, GetModelForRoute ignores any saved "us" preference
  // and routes as "fastest" — so the effective region shown here is "fastest"
  // regardless of what may be persisted on the account.
  const zdrRegionControls =
    flags["zero-data-retention-region-controls"] === true;
  const effectiveRegionMode = zdrRegionControls ? zdrRegionMode : "fastest";
  // Load the saved ZDR region preference once the card is enabled. Uses the
  // same endpoint as the toggle; failures fall back to the "fastest" default.
  useEffect(() => {
    if (!zdrFeatureFlagEnabled) return;
    let cancelled = false;
    axios
      .get("/accounts/customer/zero-data-retention")
      .then((res) => {
        if (cancelled) return;
        const mode = res?.data?.regionMode === "us" ? "us" : "fastest";
        setZdrRegionMode(mode);
      })
      .catch(() => {
        /* endpoint gated off / not opted in — keep the default */
      });
    return () => {
      cancelled = true;
    };
  }, [zdrFeatureFlagEnabled]);
  const [predictedPercentage, setPredictedPercentage] = useState<number>(0);
  const [estimatorTierSlug, setEstimatorTierSlug] = useState<string>(
    TIERS[1].slug
  );
  const [estimatorUsers, setEstimatorUsers] = useState(100);
  const [estimatorCallsPerUserPerDay, setEstimatorCallsPerUserPerDay] =
    useState<Record<string, number>>({});

  const calculatePredictedUsage = (
    startDate: number,
    endDate: number,
    currentUsage: number
  ): number => {
    const now = new Date();
    const start = new Date(startDate * 1000);
    const end = new Date(endDate * 1000);
    const totalDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    let daysPassed = (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    daysPassed = Math.max(daysPassed, 1);
    if (daysPassed <= 0) return currentUsage;
    return (currentUsage / daysPassed) * totalDays;
  };

  useEffect(() => {
    if (productSubDetail?.stripeSubscription && productSubDetail.usageInfo) {
      const predicted = calculatePredictedUsage(
        productSubDetail.stripeSubscription.current_period_start,
        productSubDetail.stripeSubscription.current_period_end,
        productSubDetail.usageInfo.units
      );
      const usageQuota = getUsageQuota(productSubDetail.usageInfo);
      setPredictedPercentage(
        usageQuota > 0 ? (predicted / usageQuota) * 100 : 0
      );
    }
  }, [productSubDetail]);

  const groupModes = [
    ["Request Type", "type"],
    ["Request Path", "path"],
    ["AI Mode", "aiMode"],
  ];
  const [usageDetailGroupMode, setUsageDetailGroupMode] = useState(
    groupModes[0]
  );
  const [usageView, setUsageView] = useState<"basic" | "graph" | "history">(
    "basic"
  );
  const [usageHistoryForPeriod, setUsageHistoryForPeriod] =
    useState<UsageHistoryReportForPeriod | null>();
  const [historyOutputMode, setHistoryOutputMode] = useState<"grouped" | "raw">(
    "grouped"
  );
  const [historyStartDate, setHistoryStartDate] = useState("");
  const [historyEndDate, setHistoryEndDate] = useState("");
  const [isSdkChatbotOpen, setIsSdkChatbotOpen] = useState(false);
  // Which Studio entry point is mid-launch, so only that button spins.
  const [studioLaunchIntent, setStudioLaunchIntent] =
    useState<StudioIntent | null>(null);
  const [activeTab, setActiveTab] = useState<DetailsTab>(() =>
    getRequestedDetailsTab()
  );
  const [showEasyAISignIn, setShowEasyAISignIn] = useState(true);
  const [isActivatingEasyAI, setIsActivatingEasyAI] = useState(false);
  const [easyAIActivationMessage, setEasyAIActivationMessage] = useState("");
  const [easyAIAutoSignInTriggered, setEasyAIAutoSignInTriggered] =
    useState(false);

  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const [usageDetail, setUsageDetail] = useState<any>(null);
  const easyAIOwnerEmail = userContext?.email ?? "";
  const isNotSubscribedError = (err: AxiosError) =>
    err?.response?.status === 404 &&
    String(err?.response?.data?.cause || "")
      .toLowerCase()
      .includes("not subscribed");

  const patchLocalSubscriptionMetadata = (
    patch: Partial<{
      refillBehaviour: "auto" | "off";
      refillBatchQuantity: number;
      autoRefillOnRenewal: number;
    }>
  ) => {
    setProductSubDetail((prev: any) => {
      if (!prev?.stripeSubscription) return prev;
      const currentMd = prev?.stripeSubscription?.metadata || {};
      const nextMd = {
        ...currentMd,
        ...(patch.refillBehaviour !== undefined
          ? { refillBehaviour: patch.refillBehaviour }
          : {}),
        ...(patch.refillBatchQuantity !== undefined
          ? { refillBatchQuantity: String(patch.refillBatchQuantity) }
          : {}),
        ...(patch.autoRefillOnRenewal !== undefined
          ? {
              autoRefillOnRenewal: String(
                Math.max(0, patch.autoRefillOnRenewal)
              ),
            }
          : {}),
      };
      return {
        ...prev,
        stripeSubscription: {
          ...prev.stripeSubscription,
          metadata: nextMd,
        },
      };
    });
    if (patch.autoRefillOnRenewal !== undefined) {
      setAutoRefillOnRenewal(Math.max(0, patch.autoRefillOnRenewal));
    }
  };

  // Platform tier-based gating via subscription metadata
  const md = productSubDetail?.stripeSubscription?.metadata;
  const metadataRefillBatchQuantity = Math.max(
    1,
    parseInt(String(md?.refillBatchQuantity ?? "1"), 10) || 1
  );
  const metadataRefillBehaviour: "auto" | "off" =
    String(md?.refillBehaviour || "auto").toLowerCase() === "off"
      ? "off"
      : "auto";
  const metadataAutoRefillOnRenewal = Math.max(
    0,
    parseInt(String(md?.autoRefillOnRenewal ?? "0"), 10) || 0
  );
  const refillSettingsDirty =
    refillBatchQuantity !== metadataRefillBatchQuantity ||
    refillBehaviour !== metadataRefillBehaviour ||
    autoRefillOnRenewal !== metadataAutoRefillOnRenewal;
  const metadataFlagEnabled = (value: unknown): boolean =>
    String(value || "").toLowerCase() === "true";
  const tierName = String(md?.tier || "").toLowerCase();
  const currentTier = TIERS.find((t) => {
    const tierSlug = t.slug.toLowerCase();
    const tierShortSlug = tierSlug.replace("nutrition-platform-", "");
    const tierDisplay = t.name.toLowerCase();
    return (
      tierName === tierDisplay ||
      tierName === tierShortSlug ||
      tierName === tierSlug
    );
  });
  // Catalog Manager: PRO plan + LaunchDarkly flag `catalog-manager`.
  // This app configures the LD provider with useCamelCaseFlagKeys: false
  // (see App.tsx), so flag keys stay kebab-case -> flags["catalog-manager"].
  const isProPlan =
    currentTier?.slug === "nutrition-platform-pro" || tierName === "pro";
  const catalogManagerFlag = flags["catalog-manager"] === true;
  const catalogManagerEnabled = isProPlan && catalogManagerFlag;

  const insightsUnlocked =
    metadataFlagEnabled(md?.insightsAccess) ||
    currentTier?.features.insights === true;
  const aiInsightsUnlocked =
    metadataFlagEnabled(md?.aiInsightsAccess) ||
    currentTier?.features.aiInsights === true;
  const modelOverridesUnlocked =
    currentTier?.features.modelSwitching === true ||
    metadataFlagEnabled(md?.modelSelectAccess) ||
    metadataFlagEnabled(md?.modelSwitchingAccess) ||
    metadataFlagEnabled(md?.modelSwitching);
  const modelOverridesLockedTooltip =
    "Upgrade to Growth to set per-route default AI models.";
  const aiInsightsLockedTooltip = "AI insights are available on Pro plans.";
  const refillPricePerM = currentTier?.refillPricePerM ?? 25;
  const currentTierDisplayName =
    currentTier?.name || formatTierDisplayName(tierName);
  const tierSubscriptionPrice = getSubscriptionMonthlyFeeUsd(
    productSubDetail?.stripeSubscription
  );
  const immediateRefillCost = refillBatchQuantity * refillPricePerM;
  const renewalRefillCost = autoRefillOnRenewal * refillPricePerM;
  const currentTierBaseTokensM = parseInt(currentTier?.tokens || "0", 10) || 0;
  const effectiveUsageQuota = Math.max(
    getUsageQuota(productSubDetail?.usageInfo),
    getUsageQuota(productState?.usageInfo)
  );
  const currentUsageTokens = Math.max(
    0,
    Number(productSubDetail?.usageInfo?.units || 0)
  );
  const projectedPeriodUsageTokens = Math.max(
    0,
    (predictedPercentage * effectiveUsageQuota) / 100
  );
  const metadataRefilledTokens = Math.max(
    0,
    (parseFloat(String(md?.refilledAmount ?? "0")) || 0) * 1000000
  );
  const metadataBaseIncludedTokens = Math.max(
    0,
    (parseFloat(String(md?.quantity ?? "0")) || 0) * 1000000
  );
  const baseIncludedTokens = Math.max(
    0,
    metadataBaseIncludedTokens || currentTierBaseTokensM * 1000000
  );
  const metadataAllowanceTokens = baseIncludedTokens + metadataRefilledTokens;
  const totalAllowanceTokens = Math.max(
    0,
    effectiveUsageQuota,
    metadataAllowanceTokens
  );
  const includedAllowanceTokens = Math.min(
    baseIncludedTokens,
    totalAllowanceTokens
  );
  const refillAllowanceTokens = Math.max(
    0,
    totalAllowanceTokens - includedAllowanceTokens
  );
  const projectedPeriodOverageTokens = Math.max(
    0,
    projectedPeriodUsageTokens - totalAllowanceTokens
  );
  const projectedPeriodOverageCost =
    refillPricePerM * Math.ceil(projectedPeriodOverageTokens / 1000000);
  const refillCostSoFar = (metadataRefilledTokens / 1000000) * refillPricePerM;
  const additionalProjectedRefillCost = projectedPeriodOverageCost;
  const totalProjectedRefillCost =
    refillCostSoFar + additionalProjectedRefillCost;
  const configuredRefillBatchCost =
    metadataRefillBatchQuantity * refillPricePerM;
  const includedUsedTokens = Math.max(
    0,
    Math.min(currentUsageTokens, includedAllowanceTokens)
  );
  const refillUsedTokens = Math.max(
    0,
    Math.min(
      currentUsageTokens - includedAllowanceTokens,
      refillAllowanceTokens
    )
  );
  // The meter represents tokens the customer actually has, plus any overage
  // they have really incurred. A projection is a forecast, not capacity: early
  // in a period it extrapolates from a single day and can reach many multiples
  // of the cap, which rescaled the entire bar and left the filled portion
  // disagreeing with the "% Used" label printed directly above it. The
  // projection marker clamps to the end of the bar instead.
  const meterCapacityTokens = Math.max(
    1,
    totalAllowanceTokens,
    currentUsageTokens
  );
  const includedAllowanceWidthPct = Math.min(
    100,
    (includedAllowanceTokens / meterCapacityTokens) * 100
  );
  const refillAllowanceWidthPct = Math.min(
    100 - includedAllowanceWidthPct,
    (refillAllowanceTokens / meterCapacityTokens) * 100
  );
  const includedUsedWidthPct = Math.min(
    includedAllowanceWidthPct,
    (includedUsedTokens / meterCapacityTokens) * 100
  );
  const refillUsedWidthPct = Math.min(
    refillAllowanceWidthPct,
    (refillUsedTokens / meterCapacityTokens) * 100
  );
  const projectedUsageWidthPct = Math.min(
    100,
    Math.max(0, (projectedPeriodUsageTokens / meterCapacityTokens) * 100)
  );
  const totalTokensAtRenewalM = currentTierBaseTokensM + autoRefillOnRenewal;
  const insightsLockedTooltip = insightsUnlocked
    ? undefined
    : "Insights & Settings are available on Growth and Pro plans.";
  const affiliateWorkflowEnabled = Boolean(
    flags["nutrition-platform-affiliate-earnings"]
  );
  const affiliateEarningsUnlocked =
    affiliateWorkflowEnabled &&
    (metadataFlagEnabled(md?.affiliateEarningsAccess) ||
      currentTier?.features?.affiliateEarnings !== false);
  const affiliateEarningsLockedTooltip = affiliateWorkflowEnabled
    ? "Affiliate earnings access is not enabled for this plan."
    : "Affiliate earnings is disabled by feature flag.";

  useEffect(() => {
    if (!affiliateWorkflowEnabled && activeTab === "affiliate") {
      setActiveTab("account");
    }
  }, [activeTab, affiliateWorkflowEnabled]);

  const isDevEnv =
    process.env.NODE_ENV === "development" ||
    process.env.REACT_APP_ENV === "development" ||
    window.location.hostname === "localhost" ||
    window.location.hostname.includes("-dev");
  const configuredEasyAIBaseUrl = String(
    process.env.REACT_APP_EASYAI_BASE_URL || ""
  )
    .trim()
    .replace(/\/+$/, "");
  const easyAIBaseUrl =
    configuredEasyAIBaseUrl ||
    (isDevEnv
      ? "https://easyai-dev.cloud.passiolife.com"
      : "https://easyai.passiolife.com");
  const easyAILoginUrl = `${easyAIBaseUrl}/login${
    easyAIOwnerEmail ? `?email=${encodeURIComponent(easyAIOwnerEmail)}` : ""
  }`;

  useEffect(() => {
    // Wait until subscription detail is loaded before enforcing insight locks.
    // Otherwise initial render (before metadata arrives) can incorrectly
    // downgrade tab=insights back to account.
    if (!productSubDetail || !productSubDetail.isSubscribed) return;
    if (activeTab === "insights" && !insightsUnlocked) {
      setActiveTab("account");
    }
    if (
      activeTab === "affiliate" &&
      (!affiliateWorkflowEnabled || !affiliateEarningsUnlocked)
    ) {
      setActiveTab("account");
    }
    if (activeTab === "catalog" && !catalogManagerEnabled) {
      setActiveTab("account");
    }
  }, [
    activeTab,
    insightsUnlocked,
    affiliateWorkflowEnabled,
    affiliateEarningsUnlocked,
    catalogManagerEnabled,
    productSubDetail,
  ]);

  useEffect(() => {
    const syncTabFromUrl = () => setActiveTab(getRequestedDetailsTab());
    window.addEventListener("popstate", syncTabFromUrl);
    window.addEventListener("hashchange", syncTabFromUrl);
    return () => {
      window.removeEventListener("popstate", syncTabFromUrl);
      window.removeEventListener("hashchange", syncTabFromUrl);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const nextHash = `#${detailsTabToHash(activeTab)}`;
    if (params.get("tab") === activeTab && window.location.hash === nextHash)
      return;
    params.set("tab", activeTab);
    const nextSearch = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${nextSearch}${nextHash}`
    );
  }, [activeTab]);

  // Data fetching - uses the "unified" endpoints since Platform shares the unified license
  const getProduct = async () =>
    await axios.get("/accounts/products/nutrition-platform/detail");
  const getProductUsageDetail = async () =>
    await axios.get(
      `/accounts/products/unified/usage/detail?groupMode=${usageDetailGroupMode[1]}&package=platform`
    );
  const getEasyAISSOURL = async () =>
    await axios.get(
      "/accounts/products/unified/easyai/sso-url?package=platform"
    );
  const getLiveUsage = async () =>
    await axios.get("/accounts/products/unified/usage/live");
  const getUsageHistoryDetail = async () => {
    const startUnix = Math.floor(
      new Date(historyStartDate + "T00:00:00").getTime() / 1000
    );
    const endUnix = Math.floor(
      new Date(historyEndDate + "T23:59:59").getTime() / 1000
    );
    return await axios.get(
      `/accounts/products/unified/usage/history?periodStart=${startUnix}&periodEnd=${endUnix}&package=platform&outputMode=${historyOutputMode}`
    );
  };

  const [usedPercentage, setUsedPercentage] = useState(0);
  const [billingPeriods, setBillingPeriods] = useState<
    { start: number; end: number }[]
  >([]);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const selectedPeriod =
    billingPeriods.length > 0 ? billingPeriods[selectedPeriodIndex] : null;

  const { isLoading: usageDetailLoading, isFetching: usageDetailFetching } =
    useQuery(
      ["platformUsageDetail", userContext?.customerid, usageDetailGroupMode[1]],
      getProductUsageDetail,
      {
        enabled: !!productSubDetail?.isSubscribed && !!userContext?.customerid,
        onError: (err: AxiosError) => {
          if (isNotSubscribedError(err)) return;
          error(
            err?.response?.data?.cause ||
              "Could not fetch the products usage detail"
          );
          if (err?.response?.status === 403) setUnauthorized(true);
        },
        onSuccess: (res: AxiosResponse) => setUsageDetail(res?.data),
      }
    );

  const { data: easyAISSOResponse } = useQuery(
    ["easyAISSOURL", userContext?.customerid, productSubDetail?.isSubscribed],
    getEasyAISSOURL,
    {
      // Not gated on the Easy AI tab any more: the Account Info tab needs to
      // know whether SSO can sign this customer in before deciding between the
      // Studio and the embedded SDK chatbot. Cached, so it costs one GET.
      enabled: !!productSubDetail?.isSubscribed && !!userContext?.customerid,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
      retry: false,
      onError: () => {
        // fall back to standard login URL without surfacing a user-facing error
      },
    }
  );

  const easyAISSOData: EasyAISSOResponse | undefined = easyAISSOResponse?.data;
  const easyAISignInUrl =
    easyAISSOData?.ssoEnabled && easyAISSOData?.ssoUrl
      ? easyAISSOData.ssoUrl
      : easyAILoginUrl;
  const [easyAIIframeUrl, setEasyAIIframeUrl] = useState(easyAILoginUrl);

  useEffect(() => {
    setEasyAIIframeUrl((prev) =>
      !prev || prev === "about:blank" ? easyAILoginUrl : prev
    );
  }, [easyAILoginUrl]);

  const resolveFreshEasyAISignInURL = async (): Promise<string> => {
    let nextURL = easyAISignInUrl;
    try {
      const res = await getEasyAISSOURL();
      const freshData: EasyAISSOResponse | undefined = res?.data;
      if (freshData?.ssoEnabled && freshData?.ssoUrl) {
        nextURL = freshData.ssoUrl;
      } else if (freshData?.loginUrl) {
        nextURL = freshData.loginUrl;
      }
    } catch {
      // Fall back to current launch URL if fresh token retrieval fails.
    }
    return nextURL;
  };

  const launchEasyAIInIframe = async () => {
    const nextURL = await resolveFreshEasyAISignInURL();
    setEasyAIIframeUrl("about:blank");
    window.requestAnimationFrame(() => setEasyAIIframeUrl(nextURL));
  };

  const openEasyAIInNewTab = async () => {
    const nextURL = await resolveFreshEasyAISignInURL();
    window.open(nextURL, "_blank", "noopener,noreferrer");
  };

  // The key the Studio should use when calling the API on the customer's
  // behalf. Prefers an active key so a launch never hands over an expired one.
  const studioLicenseKey = useMemo(() => {
    const keys = productState?.licenseKeys ?? [];
    const active = keys.find((key) => key.status === "active");
    return String(active?.key || productState?.licenseKey || "").trim();
  }, [productState?.licenseKeys, productState?.licenseKey]);

  /**
   * Opens the EasyAI Studio on the right wizard step.
   *
   * Always routes through the SSO handoff so the customer lands signed in
   * regardless of whether this browser already has an EasyAI session. The SSO
   * link deliberately carries no license — see utils/easyaiStudio — so the key
   * goes to the clipboard instead and the wizard's license step is one paste.
   */
  const launchStudio = async (intent: StudioIntent) => {
    if (studioLaunchIntent !== null) return;
    const target = STUDIO_INTENTS[intent];
    const carryLicense =
      !!studioLicenseKey && STUDIO_INTENT_NEEDS_LICENSE[intent];

    // Before window.open, and without awaiting. Opening a tab moves focus off
    // this document, and writeText on an unfocused document raises a permission
    // prompt the customer cannot see from the new tab — awaiting that prompt is
    // what left the Studio tab sitting blank until they came back and allowed
    // it. copyLicenseWithoutBlocking prefers the synchronous path so there is
    // usually no prompt at all.
    if (carryLicense) {
      copyLicenseWithoutBlocking(studioLicenseKey);
    }

    // Open the tab while the click gesture is still on the stack; the token
    // fetch below is async and a later window.open would be popup-blocked.
    const studioTab = window.open("about:blank", "_blank");
    if (studioTab) {
      try {
        // about:blank inherits our origin, so the back-reference can still be
        // severed before the tab is handed over to EasyAI.
        studioTab.opener = null;
      } catch {
        // Not fatal; EasyAI is first-party.
      }
    }

    setStudioLaunchIntent(intent);
    ReactGA.event({
      category: "EasyAI Studio",
      action: "launch",
      label: intent,
    });

    try {
      let launchUrl = "";
      try {
        const res = await getEasyAISSOURL();
        const freshData: EasyAISSOResponse | undefined = res?.data;
        if (freshData?.ssoEnabled && freshData?.ssoUrl) {
          launchUrl = withStudioRedirect(freshData.ssoUrl, target);
        }
      } catch {
        // Fall through to the direct link below.
      }

      if (!launchUrl) {
        // SSO unavailable. The direct link still lands on the right wizard for
        // anyone who already has an EasyAI session, and carries the license in
        // the fragment where it stays out of server and proxy logs.
        launchUrl = buildStudioDirectUrl(
          easyAIBaseUrl,
          target,
          carryLicense ? studioLicenseKey : null
        );
      }

      if (studioTab) {
        studioTab.location.replace(launchUrl);
      } else {
        window.open(launchUrl, "_blank", "noopener,noreferrer");
      }
    } finally {
      setStudioLaunchIntent(null);
    }
  };

  // Only fall back once we have affirmatively learned SSO is off, so a click
  // that lands before the SSO query resolves still gets the Studio.
  const easyAISSOUnavailable = easyAISSOData?.ssoEnabled === false;

  // The Studio answers SDK questions properly now (per-platform knowledge, live
  // reference pages, the v2 to v3 story, and it can scaffold an integration),
  // so it is the default. The embedded chatbot stays as the fallback: it answers
  // for anyone who loads this page, whereas the Studio needs an EasyAI session,
  // and without SSO a customer would just land on a login screen.
  const openMobileSdkAssistant = () => {
    if (STUDIO_SUPPORTS_SDK && !easyAISSOUnavailable) {
      launchStudio("mobileSdk");
      return;
    }
    setIsSdkChatbotOpen(true);
  };

  // The hero routes every intent through here so `mobileSdk` can take the
  // fallback path while the API intents go straight to the Studio.
  const handleStudioLaunch = (intent: StudioIntent) => {
    if (intent === "mobileSdk") {
      openMobileSdkAssistant();
      return;
    }
    launchStudio(intent);
  };

  useEffect(() => {
    if (activeTab !== "easyai") {
      setEasyAIAutoSignInTriggered(false);
      return;
    }
    if (
      easyAIAutoSignInTriggered ||
      !productSubDetail?.isSubscribed ||
      !userContext?.customerid
    ) {
      return;
    }
    setEasyAIAutoSignInTriggered(true);
    launchEasyAIInIframe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    easyAIAutoSignInTriggered,
    productSubDetail?.isSubscribed,
    userContext?.customerid,
  ]);

  // [Platform Product] Global route averages for tier selection pricing card (all customers, cached 6h)
  const {
    data: globalRouteAveragesResponse,
    isLoading: globalAvgLoading,
    isFetching: globalAvgFetching,
  } = useQuery(
    ["globalRouteAverages", GLOBAL_ROUTE_AVG_DAYS],
    () =>
      axios.get(
        `/accounts/products/nutrition-platform/usage/route-averages/global?days=${GLOBAL_ROUTE_AVG_DAYS}`
      ),
    {
      enabled: true,
      staleTime: 1000 * 60 * 60 * 6,
      cacheTime: 1000 * 60 * 60 * 12,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (err: AxiosError) => {
        // eslint-disable-next-line no-console
        console.warn(
          "Failed to fetch global route averages:",
          err?.response?.status,
          err?.response?.data
        );
      },
    }
  );
  const globalRouteAverages = normalizeRouteAverageRows(
    globalRouteAveragesResponse?.data
  );
  const estimatorCategories = useMemo(
    () =>
      (globalRouteAverages.length > 0
        ? globalRouteAverages.map((row) => row.category)
        : DEFAULT_ROUTE_CALCULATOR_CATEGORIES
      ).filter((value, index, all) => all.indexOf(value) === index),
    [globalRouteAverages]
  );
  useEffect(() => {
    setEstimatorCallsPerUserPerDay((prev) => {
      let changed = false;
      const next: Record<string, number> = {};
      estimatorCategories.forEach((category) => {
        if (Object.prototype.hasOwnProperty.call(prev, category)) {
          next[category] = Math.max(0, Math.floor(Number(prev[category]) || 0));
        } else {
          changed = true;
          next[category] = DEFAULT_ROUTE_CALLS_PER_USER_PER_DAY[category] ?? 0;
        }
      });
      if (Object.keys(prev).length !== estimatorCategories.length) {
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [estimatorCategories]);
  const estimatorTier =
    TIERS.find((tier) => tier.slug === estimatorTierSlug) || TIERS[1];
  const estimatorUsersSafe = Math.max(
    0,
    Math.floor(Number(estimatorUsers) || 0)
  );
  const estimatorAvgTokenMap = new Map(
    globalRouteAverages.map((row) => [
      row.category,
      Math.max(0, row.avgBilledTokens),
    ])
  );
  const estimatorRows = estimatorCategories.map((category) => {
    const callsPerUserPerDay = Math.max(
      0,
      Math.floor(Number(estimatorCallsPerUserPerDay[category]) || 0)
    );
    const avgTokensPerCall = estimatorAvgTokenMap.get(category) || 0;
    const totalCallsPerDay = estimatorUsersSafe * callsPerUserPerDay;
    const tokensPerDay = totalCallsPerDay * avgTokensPerCall;
    return {
      category,
      callsPerUserPerDay,
      avgTokensPerCall,
      totalCallsPerDay,
      tokensPerDay,
    };
  });
  const estimatorTotals = estimatorRows.reduce(
    (acc, row) => ({
      callsPerDay: acc.callsPerDay + row.totalCallsPerDay,
      tokensPerDay: acc.tokensPerDay + row.tokensPerDay,
    }),
    { callsPerDay: 0, tokensPerDay: 0 }
  );
  const estimatorProjectedMonthlyTokens = estimatorTotals.tokensPerDay * 30;
  const estimatorIncludedTokens = estimatorTier.tokensMillions * 1000000;
  const estimatorProjectedOverageTokens = Math.max(
    0,
    estimatorProjectedMonthlyTokens - estimatorIncludedTokens
  );
  const estimatorProjectedOverageCost =
    (estimatorProjectedOverageTokens / 1000000) * estimatorTier.refillPricePerM;
  const estimatorReferenceValue =
    (estimatorProjectedMonthlyTokens / 1000000) * estimatorTier.refillPricePerM;
  const estimatorIncludedUsagePct =
    estimatorIncludedTokens > 0
      ? Math.min(
          100,
          (estimatorProjectedMonthlyTokens / estimatorIncludedTokens) * 100
        )
      : 0;

  const hasUsageThisBillingPeriod =
    Number(
      productSubDetail?.usageInfo?.units ?? productState?.usageInfo?.units ?? 0
    ) > 0;

  const {
    data: customerRouteAveragesResponse,
    isLoading: customerAvgLoading,
    isFetching: customerAvgFetching,
  } = useQuery(
    ["customerRouteAverages", userContext?.customerid, GLOBAL_ROUTE_AVG_DAYS],
    () =>
      axios.get(
        `/accounts/products/nutrition-platform/usage/route-averages?days=${GLOBAL_ROUTE_AVG_DAYS}`
      ),
    {
      enabled:
        !!productSubDetail?.isSubscribed &&
        !!userContext?.customerid &&
        hasUsageThisBillingPeriod,
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (err: AxiosError) => {
        if (isNotSubscribedError(err)) return;
        // eslint-disable-next-line no-console
        console.warn(
          "Failed to fetch customer route averages:",
          err?.response?.status,
          err?.response?.data
        );
      },
    }
  );

  const customerRouteAverages = normalizeRouteAverageRows(
    customerRouteAveragesResponse?.data
  );
  const routeAverages = customerRouteAverages;
  const routeAvgLoading = customerAvgLoading;
  const routeAvgFetching = customerAvgFetching;
  const { isLoading: hisoryDataLoading, isFetching: historyDataFetching } =
    useQuery(
      [
        "platformUsageHistory",
        userContext?.customerid,
        historyStartDate,
        historyEndDate,
        historyOutputMode,
      ],
      getUsageHistoryDetail,
      {
        enabled:
          usageView === "history" && !!historyStartDate && !!historyEndDate,
        onError: (err: AxiosError) => {
          if (isNotSubscribedError(err)) return;
          error(err?.response?.data?.cause || "Could not fetch usage history");
          if (err?.response?.status === 403) setUnauthorized(true);
        },
        onSuccess: (res: AxiosResponse) => setUsageHistoryForPeriod(res?.data),
      }
    );

  const { isLoading, isFetching, refetch } = useQuery(
    ["platformProduct", userContext?.customerid],
    getProduct,
    {
      enabled: !!userContext?.customerid,
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Could not fetch the products");
        if (err?.response?.status === 403) setUnauthorized(true);
      },
      onSuccess: (res: AxiosResponse) => {
        setProductSubDetail(res?.data);
        const usageQuota = getUsageQuota(res?.data?.usageInfo);
        setUsedPercentage(
          usageQuota > 0
            ? ((res?.data?.usageInfo?.units || 0) / usageQuota) * 100
            : 0
        );
        setProductState((prev) => ({
          ...prev,
          isSubscribed: res.data?.isSubscribed,
          trialEnd: res.data?.trialEnd ? new Date(res.data?.trialEnd) : null,
          billingDate: new Date(res.data?.billingDate),
          licenseKey: res.data?.licenseKey,
          licenseKeys: res.data?.licenseKeys ?? [],
          usageInfo: res.data?.usageInfo,
          tiers: res.data?.tiers || [],
        }));
        // Populate refill settings from metadata
        const metadata = res.data?.stripeSubscription?.metadata || {};
        const renewalRefill = parseInt(
          metadata?.autoRefillOnRenewal ?? "0",
          10
        );
        const metadataBatch = parseInt(
          metadata?.refillBatchQuantity ?? "1",
          10
        );
        setAutoRefillOnRenewal(Math.max(0, renewalRefill || 0));
        setRefillBatchQuantity(Math.max(1, metadataBatch || 1));
        setRefillBehaviour(
          String(metadata?.refillBehaviour || "auto").toLowerCase() === "off"
            ? "off"
            : "auto"
        );
        setZdrEnabled(Boolean(res.data?.zeroDataRetention));
        const periods = buildBillingPeriods(res.data?.stripeSubscription);
        setBillingPeriods(periods);
        setSelectedPeriodIndex(0);
        if (periods.length > 0) {
          setHistoryStartDate(toInputDate(periods[0].start));
          setHistoryEndDate(toInputDate(periods[0].end));
        }
      },
    }
  );

  // The detail endpoint above derives usage from BigQuery, which is cached for
  // 15 minutes to keep query costs down. That cadence is right for the graphs
  // and history tables but leaves the token meter looking frozen while a
  // customer is actively spending. This polls a Redis-only endpoint so just the
  // meter stays close to live, without triggering any additional BigQuery work.
  useQuery(["platformLiveUsage", userContext?.customerid], getLiveUsage, {
    enabled: !!userContext?.customerid && !unauthorized,
    refetchInterval: LIVE_USAGE_POLL_MS,
    refetchIntervalInBackground: false,
    // A meter that misses a refresh should keep showing its last good value
    // rather than throwing an error banner over an otherwise healthy page.
    onError: () => {},
    onSuccess: (res: AxiosResponse) => {
      // 204 means nothing was cached for this customer; leave the current
      // numbers alone rather than blanking the meter.
      const usageInfo = res?.data?.usageInfo;
      if (!usageInfo) return;

      setProductSubDetail((prev: any) =>
        prev ? { ...prev, usageInfo } : prev
      );
      setProductState((prev) => ({ ...prev, usageInfo }));
      const usageQuota = getUsageQuota(usageInfo);
      setUsedPercentage(
        usageQuota > 0 ? ((usageInfo?.units || 0) / usageQuota) * 100 : 0
      );
    },
  });

  useEffect(() => {
    if (billingPeriods.length === 0) return;
    const safeIndex = Math.min(selectedPeriodIndex, billingPeriods.length - 1);
    if (safeIndex !== selectedPeriodIndex) {
      setSelectedPeriodIndex(safeIndex);
      return;
    }
    const period = billingPeriods[safeIndex];
    const nextStart = toInputDate(period.start);
    const nextEnd = toInputDate(period.end);
    if (historyStartDate !== nextStart) setHistoryStartDate(nextStart);
    if (historyEndDate !== nextEnd) setHistoryEndDate(nextEnd);
  }, [
    billingPeriods,
    selectedPeriodIndex,
    historyStartDate,
    historyEndDate,
    setHistoryStartDate,
    setHistoryEndDate,
  ]);

  const handleActivateEasyAI = async () => {
    setIsActivatingEasyAI(true);
    setEasyAIActivationMessage("");
    try {
      const res = await axios.post(
        "/accounts/products/unified/easyai/activate"
      );
      if (res?.data?.result?.status === "ok") {
        setEasyAIActivationMessage(
          "Setup email sent. Open Easy AI and use Forgot password if needed."
        );
      } else {
        // The API now returns a 502 with a readable `cause` when EasyAI
        // rejects provisioning, so this only catches an unexpected shape.
        // Deliberately not surfacing `response_body`: that was EasyAI's raw
        // JSON, which is what used to land in front of the customer.
        const fallback = "Activation failed. Please contact support.";
        setEasyAIActivationMessage(fallback);
        error(fallback);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.cause ||
        err?.response?.data?.message ||
        err?.message ||
        "Activation failed";
      setEasyAIActivationMessage(msg);
      error(msg);
    } finally {
      setIsActivatingEasyAI(false);
    }
  };

  useEffect(() => {
    const getQueryParam = (param: string) =>
      new URLSearchParams(window.location.search).get(param);
    const encodedData = getQueryParam("event");
    if (encodedData) {
      const decodedString = atob(encodedData);
      try {
        ReactGA.event(JSON.parse(decodedString));
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  const handleDownloadCSV = () => {
    const blob = new Blob([usageHistoryForPeriod?.usageCSV || ""], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const filename =
      historyOutputMode === "grouped"
        ? "usage_report_grouped.csv"
        : "usage_report_raw.csv";
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const saveRefillPreferences = async () => {
    setRefillSettingsSaving(true);
    try {
      const payload = {
        refillBehaviour,
        refillBatchQuantity,
        autoRefillOnRenewal,
      };
      await axios.put(
        "/accounts/products/nutrition-platform/subscription",
        payload
      );
      patchLocalSubscriptionMetadata(payload);
      success("Refill settings saved");
      refetch().catch(() => {});
      setTimeout(() => {
        refetch().catch(() => {});
      }, 1500);
    } catch (err: any) {
      error(err?.response?.data?.cause || "Failed to save refill settings");
    } finally {
      setRefillSettingsSaving(false);
    }
  };

  const saveZeroDataRetention = async (nextEnabled: boolean) => {
    setZdrSaving(true);
    try {
      const res = await axios.patch("/accounts/customer/zero-data-retention", {
        enabled: nextEnabled,
        regionMode: zdrRegionMode,
      });
      const newEnabled = Boolean(res?.data?.enabled);
      if (res?.data?.regionMode) {
        setZdrRegionMode(res.data.regionMode === "us" ? "us" : "fastest");
      }
      setZdrEnabled(newEnabled);
      setProductSubDetail((prev: any) =>
        prev ? { ...prev, zeroDataRetention: newEnabled } : prev
      );
      success(
        newEnabled
          ? "Zero Data Retention enabled. Only Vertex-routed AI models will be used."
          : "Zero Data Retention disabled. All available models are now usable."
      );
      // Force a refetch so any cached non-compliant model overrides surface in
      // the Insights tab the next time the user views it.
      refetch().catch(() => {});
    } catch (err: any) {
      error(
        err?.response?.data?.cause ||
          err?.response?.data?.message ||
          "Failed to update Zero Data Retention setting"
      );
    } finally {
      setZdrSaving(false);
      setZdrConfirmAction(null);
    }
  };

  const saveZdrRegion = async (nextMode: "fastest" | "us") => {
    // Guard: the region selector is locked until the LaunchDarkly region-
    // controls flag is on. The radios are already disabled in that state; this
    // is belt-and-suspenders so a stray call can't persist a "us" preference
    // the backend would ignore anyway.
    if (!zdrRegionControls) return;
    if (nextMode === zdrRegionMode) return;
    const previous = zdrRegionMode;
    setZdrRegionMode(nextMode); // optimistic
    setZdrRegionSaving(true);
    try {
      const res = await axios.patch("/accounts/customer/zero-data-retention", {
        enabled: zdrEnabled,
        regionMode: nextMode,
      });
      const saved = res?.data?.regionMode === "us" ? "us" : "fastest";
      setZdrRegionMode(saved);
      success(
        saved === "us"
          ? "Data region set to United States only. Eligible models will be pinned to US regions."
          : "Data region set to Fastest available. Eligible models will use the lowest-latency endpoints."
      );
    } catch (err: any) {
      setZdrRegionMode(previous); // revert on failure
      error(
        err?.response?.data?.cause ||
          err?.response?.data?.message ||
          "Failed to update data region preference"
      );
    } finally {
      setZdrRegionSaving(false);
    }
  };

  const handleRefillNow = async () => {
    setRefillNowLoading(true);
    try {
      await axios.post(
        "/accounts/products/nutrition-platform/subscription/refill"
      );
      success(
        "Refill processed successfully. Usage stats may take a few minutes to update."
      );
      await new Promise((resolve) => setTimeout(resolve, 2000));
      refetch().catch(() => {});
    } catch (err: any) {
      error(err?.response?.data?.cause || "Failed to refill");
    } finally {
      setRefillNowLoading(false);
    }
  };

  if (unauthorized) return <Navigate to="/access-restricted" />;

  // =========================================================================
  // NOT SUBSCRIBED - show tier pricing cards
  // Also show tier cards when user has no customerid (new user, query is disabled)
  // =========================================================================
  const queryDisabled = !userContext?.customerid;
  if (
    queryDisabled ||
    (!isFetching && !isLoading && !productState.isSubscribed)
  ) {
    return (
      <>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="font-bold text-2xl xl:text-3xl text-gray-900">
              Nutrition AI Platform
            </h2>
            <p className="text-gray-500 mt-1">
              API access, mobile SDKs, Easy AI, and more
            </p>
          </div>
        </div>
        <p className="text-gray-600 mb-8 text-lg">
          Choose the plan that fits your needs. All plans include Easy AI, SDK
          access, localization, and the full REST API.
        </p>
        <div className="flex flex-wrap place-content-evenly items-stretch h-full gap-y-4 mb-8">
          {TIERS.map((tier) => (
            <SubscriptionCard
              key={tier.slug}
              name={tier.name}
              price={
                <>
                  <div className="text-gray-900 font-inter text-6xl font-extrabold leading-[60px]">
                    {tier.price}
                  </div>
                  <div className="text-gray-500 font-inter text-2xl font-medium leading-8">
                    /mo
                  </div>
                </>
              }
              counterInput={<></>}
              description="Choose this plan for your expected usage and feature needs."
              descriptionAlt={`${tier.refillPrice} refill`}
              bulletsHtml={[
                <span key={`${tier.slug}-included`}>
                  <span className="font-extrabold">{tier.tokens} </span>
                  <span>tokens per month included</span>
                </span>,
                ...(tier.features.easyAI
                  ? [
                      <span key={`${tier.slug}-easy`}>
                        Easy AI + API + SDK access
                      </span>,
                    ]
                  : []),
                ...(tier.features.localization
                  ? [
                      <span key={`${tier.slug}-localization`}>
                        Localization
                      </span>,
                    ]
                  : []),
                ...(tier.features.modelSwitching
                  ? [
                      <span key={`${tier.slug}-model-switching`}>
                        Model Switching
                      </span>,
                    ]
                  : []),
                ...(tier.features.insights
                  ? [<span key={`${tier.slug}-insights`}>Usage Insights</span>]
                  : []),
                ...(tier.features.aiInsights
                  ? [
                      <span key={`${tier.slug}-ai-insights`}>
                        AI-Powered Insights
                      </span>,
                    ]
                  : []),
                <span key={`${tier.slug}-support`}>
                  {tier.features.support}
                </span>,
              ]}
              bullets={[]}
              isCurrent={false}
              buttonText="Get Started"
              buttonOnClick={() =>
                navigate(`/manage-subscription?product=${tier.slug}`)
              }
              isCurrentButtonText="Get Started"
              isCurrentButtonOnClick={() =>
                navigate(`/manage-subscription?product=${tier.slug}`)
              }
              showCancelButton={false}
              cancelButtonOnClick={() => {}}
              disabled={false}
              wide={false}
              compact
            />
          ))}
        </div>

        <Card className="sm:p-6 mb-8 border-2 border-indigo-200 shadow-sm">
          <p className="font-semibold text-gray-900 mb-1">
            Monthly Usage Estimator (Calls to Tokens)
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Estimate monthly token usage before subscribing. This helps you
            compare included tokens vs projected refill overage.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                Estimate Against Tier
              </label>
              <select
                value={estimatorTierSlug}
                onChange={(e) => setEstimatorTierSlug(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              >
                {TIERS.map((tier) => (
                  <option key={tier.slug} value={tier.slug}>
                    {tier.name} ({tier.tokens} included, {tier.refillPrice}{" "}
                    refill)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                Active Users
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={estimatorUsers}
                onChange={(e) =>
                  setEstimatorUsers(Math.max(0, Number(e.target.value) || 0))
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                Refill Rate Used
              </label>
              <div className="h-[42px] rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 flex items-center">
                {estimatorTier.refillPrice}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-100 text-left text-gray-600 text-xs uppercase tracking-wider">
                  <th className="py-2 pr-4">Route Category</th>
                  <th className="py-2 pr-4 text-right">Avg Tokens / Call</th>
                  <th className="py-2 pr-4 text-right">Calls/User/Day</th>
                  <th className="py-2 pr-4 text-right">Total Calls/Day</th>
                  <th className="py-2 text-right">Estimated Tokens/Day</th>
                </tr>
              </thead>
              <tbody>
                {estimatorRows.map((row) => (
                  <tr
                    key={row.category}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-2.5 pr-4 text-gray-900">
                      {row.category}
                    </td>
                    <td className="py-2.5 pr-4 text-right text-gray-700">
                      {Math.round(row.avgTokensPerCall).toLocaleString()}
                    </td>
                    <td className="py-2.5 pr-4 text-right">
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={row.callsPerUserPerDay}
                        onChange={(e) =>
                          setEstimatorCallsPerUserPerDay((prev) => ({
                            ...prev,
                            [row.category]: Math.max(
                              0,
                              Math.floor(Number(e.target.value) || 0)
                            ),
                          }))
                        }
                        className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm text-right text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="py-2.5 pr-4 text-right text-gray-700">
                      {Math.round(row.totalCallsPerDay).toLocaleString()}
                    </td>
                    <td className="py-2.5 text-right font-medium text-gray-900">
                      {formatTokenCount(row.tokensPerDay)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 font-semibold text-gray-900">
                  <td className="py-2.5 pr-4">Total / day</td>
                  <td className="py-2.5 pr-4 text-right">—</td>
                  <td className="py-2.5 pr-4 text-right">—</td>
                  <td className="py-2.5 pr-4 text-right">
                    {Math.round(estimatorTotals.callsPerDay).toLocaleString()}
                  </td>
                  <td className="py-2.5 text-right">
                    {formatTokenCount(estimatorTotals.tokensPerDay)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs uppercase tracking-wider text-gray-500">
                Estimated Tokens / 30 Days
              </p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {formatTokenCount(estimatorProjectedMonthlyTokens)}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs uppercase tracking-wider text-gray-500">
                Included Tokens / 30 Days
              </p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {formatTokenCount(estimatorIncludedTokens)}
              </p>
              <p className="text-xs text-gray-500">
                {estimatorIncludedUsagePct.toFixed(1)}% of included used
              </p>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs uppercase tracking-wider text-gray-500">
                Projected Additional Refill Cost
              </p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {estimatorProjectedOverageCost.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </p>
              <p className="text-xs text-gray-500">
                {formatTokenCount(estimatorProjectedOverageTokens)} extra tokens
                at {estimatorTier.refillPrice}/1M
              </p>
            </div>
          </div>
          <EstimatedMonthlyCost
            tierName={estimatorTier.name}
            subscriptionPrice={estimatorTier.priceNum}
            includedTokensMillions={estimatorTier.tokensMillions}
            projectedOverageCost={estimatorProjectedOverageCost}
          />
          <p className="mt-3 text-xs text-gray-500">
            Reference value for all estimated usage (before included tokens):{" "}
            {estimatorReferenceValue.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
            . Actual refill billing applies only to usage above included monthly
            tokens.
          </p>
        </Card>

        {/* Estimated cost per 1K calls table */}
        <Card className="sm:p-6 mb-8">
          <p className="font-semibold text-gray-900 mb-1">
            Route Cost Benchmarks
          </p>
          <p className="text-sm text-gray-500 mb-4">
            {globalRouteAverages.length > 0
              ? `Shows estimated refill value for 1,000 calls by route category using platform averages from the last ${GLOBAL_ROUTE_AVG_DAYS} days.`
              : "Loading platform-wide average token usage from recent production requests."}
          </p>
          <div className="mb-4 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
            <span className="font-semibold">Reference only:</span> this helps
            compare route efficiency. Refill billing starts only after included
            monthly tokens are used.
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100">
                <tr className="border-b border-gray-200 text-left text-gray-600 text-xs uppercase tracking-wider">
                  <th className="py-2 pr-4">Route Type</th>
                  <th className="py-2 pr-4 text-right">
                    Avg Tokens / Call (Platform 30d)
                    <HeaderHelp text="Average billed tokens for one request in this route category, using platform-wide data from the last 30 days." />
                  </th>
                  {TIERS.map((t) => (
                    <th key={t.name} className="py-2 text-right">
                      {t.name}
                      <br />
                      <span className="text-xs normal-case font-normal">
                        Cost for 1,000 calls ({t.refillPrice})
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {globalRouteAverages
                  .map((r) => ({
                    route: r.category,
                    avgTokens: Math.round(r.avgBilledTokens),
                  }))
                  .map((row) => (
                    <tr
                      key={row.route}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-2.5 pr-4 text-gray-900">{row.route}</td>
                      <td className="py-2.5 pr-4 text-right font-medium text-indigo-600">
                        {globalAvgLoading || globalAvgFetching
                          ? "..."
                          : `~${row.avgTokens.toLocaleString()}`}
                      </td>
                      {TIERS.map((t) => (
                        <td
                          key={t.name}
                          className="py-2.5 text-right text-gray-700"
                        >
                          {(
                            (row.avgTokens * t.refillPricePerM * 1000) /
                            1000000
                          ).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      ))}
                    </tr>
                  ))}
                {!globalAvgLoading &&
                  !globalAvgFetching &&
                  globalRouteAverages.length === 0 && (
                    <tr>
                      <td
                        colSpan={2 + TIERS.length}
                        className="py-4 text-center text-sm text-gray-500"
                      >
                        No route-average data returned for the selected window.
                      </td>
                    </tr>
                  )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300">
                  <td
                    colSpan={2 + TIERS.length}
                    className="pt-3 text-xs text-gray-400"
                  >
                    Calls with billed tokens &gt;= 2,500,000 are excluded as
                    outliers.
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      </>
    );
  }

  // =========================================================================
  // SUBSCRIBED - full dashboard (mirrors Hub layout)
  // =========================================================================
  const currentPeriodStartUnix =
    billingPeriods[0]?.start ??
    Number(productSubDetail?.stripeSubscription?.current_period_start || 0);
  const currentPeriodEndUnix =
    billingPeriods[0]?.end ??
    Number(productSubDetail?.stripeSubscription?.current_period_end || 0);

  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h2 className="font-bold inter-bold text-md md:text-lg xl:text-3xl">
          Nutrition AI Platform Details
          {currentTier && (
            <span className={`ml-3 ${TIER_PILL_CLASS}`}>
              {currentTier.name}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={() =>
            navigate(`/manage-subscription?product=${NUTRITION_PLATFORM_SLUG}`)
          }
          className="inline-flex items-center rounded-md border border-indigo-300 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
        >
          Manage Subscription
        </button>
      </div>

      {/* ===== Tabs ===== */}
      <div className="mb-6">
        <div className="flex items-end gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("account")}
            className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 ${
              activeTab === "account"
                ? "bg-white text-indigo-700 border-gray-200"
                : "bg-gray-100 text-gray-600 border-transparent hover:text-indigo-600"
            }`}
          >
            Account Info
          </button>
          <button
            onClick={() => setActiveTab("easyai")}
            className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 relative ${
              activeTab === "easyai"
                ? "bg-gradient-to-r from-[#f7fee7] to-[#ecfccb] text-indigo-700 border-indigo-200"
                : "bg-gradient-to-r from-gray-100 to-[#f7fee7] text-gray-700 border-transparent hover:text-indigo-600 hover:border-indigo-100"
            }`}
          >
            <span className="flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 text-yellow-500" />
              Easy AI
              <span className="absolute -top-2 -right-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse shadow-lg">
                NEW
              </span>
            </span>
          </button>
          <span title={insightsLockedTooltip}>
            <button
              onClick={() => {
                if (insightsUnlocked) setActiveTab("insights");
              }}
              className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 ${
                activeTab === "insights"
                  ? "bg-white text-indigo-700 border-gray-200"
                  : "bg-gray-100 text-gray-600 border-transparent hover:text-indigo-600"
              } ${
                insightsUnlocked
                  ? ""
                  : "cursor-not-allowed text-gray-400 hover:text-gray-400"
              }`}
              aria-disabled={!insightsUnlocked}
            >
              <span className="inline-flex items-center gap-2">
                Insights & Settings
                {!insightsUnlocked && (
                  <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600">
                    Locked
                  </span>
                )}
              </span>
            </button>
          </span>
          {affiliateWorkflowEnabled && (
            <span title={affiliateEarningsLockedTooltip}>
              <button
                onClick={() => {
                  if (affiliateEarningsUnlocked) setActiveTab("affiliate");
                }}
                className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 ${
                  activeTab === "affiliate"
                    ? "bg-white text-indigo-700 border-gray-200"
                    : "bg-gray-100 text-gray-600 border-transparent hover:text-indigo-600"
                } ${
                  affiliateEarningsUnlocked
                    ? ""
                    : "cursor-not-allowed text-gray-400 hover:text-gray-400"
                }`}
                aria-disabled={!affiliateEarningsUnlocked}
              >
                <span className="inline-flex items-center gap-2">
                  Affiliate Earnings
                  {!affiliateEarningsUnlocked && (
                    <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600">
                      Locked
                    </span>
                  )}
                </span>
              </button>
            </span>
          )}
          {catalogManagerEnabled && (
            <button
              onClick={() => setActiveTab("catalog")}
              className={`px-6 py-3 text-lg font-semibold rounded-t-lg border border-b-0 ${
                activeTab === "catalog"
                  ? "bg-white text-indigo-700 border-gray-200"
                  : "bg-gray-100 text-gray-600 border-transparent hover:text-indigo-600"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                Catalog Manager
                <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                  PRO
                </span>
              </span>
            </button>
          )}
        </div>
      </div>

      {/* ===== Catalog Manager Tab (PRO + flag) ===== */}
      {catalogManagerEnabled && (
        <div className={activeTab === "catalog" ? "block" : "hidden"}>
          <CatalogManager customerName={userContext?.email ?? undefined} />
        </div>
      )}

      {/* ===== Account Info Tab ===== */}
      <div
        className={`gap-6 ${
          activeTab === "account" ? "flex flex-col" : "hidden"
        }`}
      >
        {/* --- EasyAI Studio --- The headline entry point for building
            against the API. Deliberately above everything else: it is where
            we want both new and returning integrators to start, and it
            replaces the old Integration Assistant chatbot overlay. */}
        <EasyAIStudioCard
          onLaunch={handleStudioLaunch}
          launchingIntent={studioLaunchIntent}
          hasLicenseKey={!!studioLicenseKey}
        />

        {/* --- Two independent columns rather than a two-column grid. A grid
            sizes every card in a row to the tallest one, which is what left
            the shorter card of each pair padded out with dead space (most
            visibly the ZDR card sitting next to an expanded plan card). Each
            column here is its own flex stack, so cards are only as tall as
            their content and `items-start` keeps the columns from stretching
            each other. Collapses to one column below `lg`, preserving the
            original reading order. --- */}
        <div className="grid items-start gap-6 lg:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-6">
            {/* --- Usage Card --- */}
            <Card className="sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-col">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Current Period
                  </h3>
                  <span className="text-sm text-gray-500">
                    {currentPeriodStartUnix > 0 && currentPeriodEndUnix > 0 ? (
                      <>
                        {new Date(
                          currentPeriodStartUnix * 1000
                        ).toLocaleDateString("default", {
                          month: "2-digit",
                          year: "2-digit",
                          day: "2-digit",
                        })}
                        {"  -  "}
                        {new Date(
                          currentPeriodEndUnix * 1000
                        ).toLocaleDateString("default", {
                          month: "2-digit",
                          year: "2-digit",
                          day: "2-digit",
                        })}
                      </>
                    ) : (
                      "Unavailable"
                    )}
                  </span>
                </div>
                <div className="flex flex-col items-start gap-2 sm:items-end">
                  {!isFetching && !isLoading ? (
                    productState.trialEnd ? (
                      <Badge isWarning className="px-3 py-1.5">
                        <ClockIcon
                          className="h-5 w-5 flex-shrink-0 mr-2"
                          aria-hidden="true"
                        />
                        Free Trial Ends on{" "}
                        {productState.trialEnd.toLocaleDateString()}
                      </Badge>
                    ) : (
                      <Badge className="px-3 py-1.5">
                        <ClockIcon
                          className="h-5 w-5 flex-shrink-0 mr-2"
                          aria-hidden="true"
                        />
                        Billed Next on{" "}
                        {productState.billingDate.toLocaleDateString()}
                      </Badge>
                    )
                  ) : (
                    <></>
                  )}
                  {currentTier && (
                    <span className={TIER_PILL_CLASS}>
                      Tier: {currentTier.name}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-4 mt-4">
                <div className="flex flex-col justify-center items-center w-full">
                  <div className="flex flex-col items-center w-full mb-2">
                    <div className="flex space-x-2 mb-8">
                      {(["basic", "graph", "history"] as const).map((v) => (
                        <button
                          key={v}
                          onClick={() => setUsageView(v)}
                          className={`px-3 py-1 text-sm font-medium rounded-full ${
                            usageView === v
                              ? "bg-slate-700 text-white"
                              : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                          }`}
                        >
                          {v.charAt(0).toUpperCase() + v.slice(1)}
                        </button>
                      ))}
                    </div>

                    {isLoading || isFetching ? (
                      <Spinner className="!w-[2rem]" />
                    ) : productSubDetail?.usageInfo?.units !== undefined ? (
                      <>
                        {/* ----- Basic View ----- */}
                        {usageView === "basic" && (
                          <>
                            <div className="text-passio-indigo text-md font-bold">
                              {convertToNumLetter(
                                productSubDetail.usageInfo.units
                              )}{" "}
                              /{" "}
                              {convertToNumLetter(
                                productSubDetail.usageInfo.unitsQuota
                              )}
                            </div>
                            <div className="flex justify-between w-full text-slate-700 mb-1">
                              <span className="text-sm font-semibold">
                                {usedPercentage.toFixed(1)}% Used (
                                {predictedPercentage.toFixed(1)}% Projected for
                                period)
                              </span>
                              {usedPercentage < 100 && (
                                <span className="text-sm font-semibold">
                                  {(100 - usedPercentage).toFixed(1)}% Available
                                </span>
                              )}
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-6 relative overflow-hidden border border-gray-200">
                              <div
                                className="absolute inset-y-0 left-0 bg-indigo-100"
                                style={{
                                  width: `${includedAllowanceWidthPct}%`,
                                }}
                              />
                              <div
                                className="absolute inset-y-0 bg-amber-100"
                                style={{
                                  left: `${includedAllowanceWidthPct}%`,
                                  width: `${refillAllowanceWidthPct}%`,
                                }}
                              />
                              <div
                                className="absolute inset-y-0 left-0 bg-indigo-600"
                                style={{ width: `${includedUsedWidthPct}%` }}
                              />
                              {refillUsedWidthPct > 0 && (
                                <div
                                  className="absolute inset-y-0 bg-amber-600"
                                  style={{
                                    left: `${includedAllowanceWidthPct}%`,
                                    width: `${refillUsedWidthPct}%`,
                                  }}
                                />
                              )}
                              <div
                                className="absolute inset-y-0 border-l-2 border-amber-500"
                                style={{
                                  left: `calc(${Math.max(
                                    1,
                                    projectedUsageWidthPct
                                  )}% - 1px)`,
                                }}
                              />
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-700">
                              <span className="inline-flex items-center gap-1">
                                <span className="h-2.5 w-2.5 rounded-full bg-indigo-600" />
                                Used Included Tokens
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <span className="h-2.5 w-2.5 rounded-full bg-amber-600" />
                                Used Refill Tokens
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <span className="h-2.5 w-2.5 rounded-full bg-indigo-100 border border-indigo-200" />
                                Included Tokens
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <span className="h-2.5 w-2.5 rounded-full bg-amber-100 border border-amber-200" />
                                Refilled Tokens
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <span className="h-3 w-0 border-l-2 border-amber-500" />
                                Projected Usage
                              </span>
                            </div>
                          </>
                        )}

                        {/* ----- History View ----- */}
                        {usageView === "history" && (
                          <>
                            {!historyDataFetching && !hisoryDataLoading ? (
                              <div className="space-y-4 w-full">
                                <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4">
                                  <label
                                    htmlFor="platform-billing-period"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                  >
                                    Billing Period
                                  </label>
                                  {billingPeriods.length > 0 ? (
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() =>
                                          setSelectedPeriodIndex((prev) =>
                                            Math.min(
                                              prev + 1,
                                              billingPeriods.length - 1
                                            )
                                          )
                                        }
                                        disabled={
                                          selectedPeriodIndex >=
                                          billingPeriods.length - 1
                                        }
                                        className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                        title="Previous billing period"
                                      >
                                        <ChevronLeftIcon className="w-4 h-4" />
                                      </button>
                                      <select
                                        id="platform-billing-period"
                                        className="flex-1 pl-3 pr-10 py-2 text-sm border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                        value={selectedPeriodIndex}
                                        onChange={(e) =>
                                          setSelectedPeriodIndex(
                                            parseInt(e.target.value, 10)
                                          )
                                        }
                                      >
                                        {billingPeriods.map((period, index) => (
                                          <option key={index} value={index}>
                                            {new Date(
                                              period.start * 1000
                                            ).toLocaleDateString()}{" "}
                                            -{" "}
                                            {new Date(
                                              period.end * 1000
                                            ).toLocaleDateString()}
                                            {index === 0 ? " (current)" : ""}
                                          </option>
                                        ))}
                                      </select>
                                      <button
                                        onClick={() =>
                                          setSelectedPeriodIndex((prev) =>
                                            Math.max(prev - 1, 0)
                                          )
                                        }
                                        disabled={selectedPeriodIndex <= 0}
                                        className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                        title="Next billing period"
                                      >
                                        <ChevronRightIcon className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-500">
                                      Billing periods are not available yet for
                                      this subscription.
                                    </p>
                                  )}
                                  {selectedPeriod && (
                                    <p className="text-xs text-gray-500 mt-2">
                                      Loading usage for{" "}
                                      {new Date(
                                        selectedPeriod.start * 1000
                                      ).toLocaleDateString()}{" "}
                                      through{" "}
                                      {new Date(
                                        selectedPeriod.end * 1000
                                      ).toLocaleDateString()}
                                      .
                                    </p>
                                  )}
                                </div>

                                <div className="flex flex-wrap items-center gap-3 justify-between">
                                  <div className="inline-flex rounded-md shadow-sm">
                                    <button
                                      onClick={() =>
                                        setHistoryOutputMode("grouped")
                                      }
                                      className={`px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                                        historyOutputMode === "grouped"
                                          ? "bg-indigo-600 text-white border-indigo-600"
                                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                      }`}
                                    >
                                      Grouped Output
                                    </button>
                                    <button
                                      onClick={() =>
                                        setHistoryOutputMode("raw")
                                      }
                                      className={`px-3 py-1.5 text-sm font-medium rounded-r-md border-t border-b border-r ${
                                        historyOutputMode === "raw"
                                          ? "bg-indigo-600 text-white border-indigo-600"
                                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                      }`}
                                    >
                                      Raw Output
                                    </button>
                                  </div>
                                  <Button
                                    onClick={handleDownloadCSV}
                                    className="whitespace-nowrap"
                                    disabled={historyDataFetching}
                                  >
                                    <ArrowDownTrayIcon className="w-4 h-4 mr-1.5 inline" />
                                    Download CSV
                                  </Button>
                                </div>
                                {historyOutputMode === "raw" && (
                                  <p className="text-xs text-amber-600">
                                    Raw output includes every individual API
                                    call. For high-usage periods this may be a
                                    large file.
                                  </p>
                                )}

                                {/* Invoice Links */}
                                {usageHistoryForPeriod?.invoiceLinks &&
                                  usageHistoryForPeriod.invoiceLinks.length >
                                    0 && (
                                    <div>
                                      <h3 className="text-sm font-medium text-gray-900 mb-2">
                                        Invoice Downloads
                                      </h3>
                                      <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
                                        {usageHistoryForPeriod.invoiceLinks.map(
                                          (link, index) => (
                                            <li
                                              key={`inv-${index}`}
                                              className="px-4 py-2 hover:bg-gray-50"
                                            >
                                              <a
                                                href={link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-indigo-600 hover:text-indigo-800 flex items-center text-sm"
                                              >
                                                <ArrowDownTrayIcon className="pr-2 w-5 h-5" />
                                                Download Period Invoice{" "}
                                                {index + 1}
                                              </a>
                                            </li>
                                          )
                                        )}
                                      </ul>
                                    </div>
                                  )}
                              </div>
                            ) : (
                              <Spinner className="!w-[2rem]" />
                            )}
                          </>
                        )}

                        {/* ----- Graph View ----- */}
                        {usageView === "graph" && (
                          <div className="w-full mt-4 items-center align-middle content-center">
                            <Plot
                              data={[
                                {
                                  type: "bar" as const,
                                  x: [
                                    "Used",
                                    "Remaining",
                                    "Avg/Day",
                                    "Projected Usage",
                                    "Projected Overage",
                                  ],
                                  y: [
                                    productSubDetail.usageInfo.units,
                                    productSubDetail.usageInfo.unitsQuota -
                                      productSubDetail.usageInfo.units,
                                    productSubDetail.usageInfo.units /
                                      ((new Date().getTime() -
                                        new Date(
                                          productSubDetail.stripeSubscription
                                            .current_period_start * 1000
                                        ).getTime()) /
                                        (1000 * 60 * 60 * 24)),
                                    (predictedPercentage *
                                      productSubDetail.usageInfo.unitsQuota) /
                                      100,
                                    Math.max(
                                      0,
                                      (predictedPercentage *
                                        productSubDetail.usageInfo.unitsQuota) /
                                        100 -
                                        productSubDetail.usageInfo.unitsQuota
                                    ),
                                  ],
                                  text: [
                                    convertToNumLetter(
                                      productSubDetail.usageInfo.units
                                    ),
                                    convertToNumLetter(
                                      productSubDetail.usageInfo.unitsQuota -
                                        productSubDetail.usageInfo.units
                                    ),
                                    convertToNumLetter(
                                      productSubDetail.usageInfo.units /
                                        ((new Date().getTime() -
                                          new Date(
                                            productSubDetail.stripeSubscription
                                              .current_period_start * 1000
                                          ).getTime()) /
                                          (1000 * 60 * 60 * 24))
                                    ),
                                    convertToNumLetter(
                                      (predictedPercentage *
                                        productSubDetail.usageInfo.unitsQuota) /
                                        100
                                    ),
                                    convertToNumLetter(
                                      Math.max(
                                        0,
                                        (predictedPercentage *
                                          productSubDetail.usageInfo
                                            .unitsQuota) /
                                          100 -
                                          productSubDetail.usageInfo.unitsQuota
                                      )
                                    ),
                                  ],
                                  textposition: "auto" as const,
                                  hoverinfo: "text" as const,
                                  marker: {
                                    // Semantic: Used/Avg-Day neutral, Remaining good,
                                    // Projected Usage caution, Projected Overage bad.
                                    color: [
                                      "#64748b",
                                      "#4d7c0f",
                                      "#94a3b8",
                                      "#f59e0b",
                                      "#dc2626",
                                    ],
                                  },
                                  yaxis: "y" as const,
                                },
                                {
                                  type: "bar" as const,
                                  x: ["Days Left"],
                                  y: [
                                    Math.ceil(
                                      (new Date(
                                        productSubDetail.stripeSubscription
                                          .current_period_end * 1000
                                      ).getTime() -
                                        new Date().getTime()) /
                                        (1000 * 60 * 60 * 24)
                                    ),
                                  ],
                                  text: [
                                    Math.ceil(
                                      (new Date(
                                        productSubDetail.stripeSubscription
                                          .current_period_end * 1000
                                      ).getTime() -
                                        new Date().getTime()) /
                                        (1000 * 60 * 60 * 24)
                                    ).toString() + " days",
                                  ],
                                  textposition: "auto" as const,
                                  hoverinfo: "text" as const,
                                  marker: { color: "#64748b" },
                                  yaxis: "y2" as const,
                                },
                              ]}
                              layout={{
                                title: "Token Usage Overview",
                                font: { size: 10 },
                                autosize: true,
                                height: 300,
                                margin: { l: 40, r: 40, t: 40, b: 80 },
                                bargap: 0.15,
                                yaxis: {
                                  title: "Tokens",
                                  side: "left" as const,
                                  range: [
                                    0,
                                    Math.max(
                                      productSubDetail.usageInfo.unitsQuota,
                                      (predictedPercentage *
                                        productSubDetail.usageInfo.unitsQuota) /
                                        100
                                    ),
                                  ],
                                },
                                yaxis2: {
                                  title: "Billing Period Days",
                                  side: "right" as const,
                                  overlaying: "y" as const,
                                  rangemode: "tozero" as const,
                                  range: [
                                    0,
                                    Math.max(
                                      31,
                                      Math.ceil(
                                        (new Date(
                                          productSubDetail.stripeSubscription
                                            .current_period_end * 1000
                                        ).getTime() -
                                          new Date().getTime()) /
                                          (1000 * 60 * 60 * 24)
                                      )
                                    ),
                                  ],
                                  dtick: 5,
                                },
                                showlegend: false,
                              }}
                              useResizeHandler={true}
                              style={{ width: "auto", height: "220px" }}
                              config={{
                                displayModeBar: false,
                                responsive: true,
                              }}
                            />
                          </div>
                        )}

                        {/* Projected Overage — only when projected > available */}
                        {projectedPeriodOverageTokens > 0 && (
                          <div className="w-full mt-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm">
                            <p className="font-semibold text-red-700 mb-2">
                              Projected to exceed available tokens
                            </p>
                            <div className="w-full bg-red-100 rounded-full h-4 relative overflow-hidden mb-3">
                              <div
                                className="absolute inset-y-0 left-0 bg-slate-500 rounded-l-full"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (currentUsageTokens /
                                      Math.max(1, projectedPeriodUsageTokens)) *
                                      100
                                  )}%`,
                                }}
                              />
                              <div
                                className="absolute inset-y-0 border-l-2 border-emerald-600"
                                style={{
                                  left: `${Math.min(
                                    100,
                                    (totalAllowanceTokens /
                                      Math.max(1, projectedPeriodUsageTokens)) *
                                      100
                                  )}%`,
                                }}
                              />
                            </div>
                            <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-3">
                              <span className="inline-flex items-center gap-1">
                                <span className="h-2 w-2 rounded-full bg-slate-500" />
                                Used ({convertToNumLetter(currentUsageTokens)})
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <span className="h-2.5 w-0 border-l-2 border-emerald-600" />
                                Available (
                                {convertToNumLetter(totalAllowanceTokens)})
                              </span>
                              <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                                Projected:{" "}
                                {convertToNumLetter(projectedPeriodUsageTokens)}
                              </span>
                            </div>
                            <div className="space-y-1 text-red-800">
                              <div className="flex justify-between">
                                <span>Additional tokens needed</span>
                                <span className="font-semibold">
                                  {convertToNumLetter(
                                    projectedPeriodOverageTokens
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Additional refill cost</span>
                                <span className="font-semibold">
                                  {additionalProjectedRefillCost.toLocaleString(
                                    "en-US",
                                    { style: "currency", currency: "USD" }
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between font-bold border-t border-red-200 pt-1">
                                <span>Total projected refill cost</span>
                                <span>
                                  {totalProjectedRefillCost.toLocaleString(
                                    "en-US",
                                    {
                                      style: "currency",
                                      currency: "USD",
                                    }
                                  )}
                                </span>
                              </div>
                            </div>
                            <p className="mt-2 text-xs text-red-600">
                              {productSubDetail.stripeSubscription.metadata
                                .refillBehaviour === "auto"
                                ? `Auto-refill is enabled at ${
                                    currentTier?.refillPrice ??
                                    "$" + refillPricePerM
                                  } per 1M tokens.`
                                : "Auto-refill is off. Consider enabling it or purchasing tokens manually to avoid service interruption."}
                            </p>
                          </div>
                        )}
                        <span className="mt-2 text-sm text-gray-700">
                          *Usage and predictions in this dashboard may take a
                          few minutes to reflect the latest data. Your API
                          client responses will be up-to-date.
                        </span>
                      </>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
              </div>
            </Card>

            <PlatformLicenseKeysCard
              licenseKeys={productState?.licenseKeys ?? []}
              onChange={async () => {
                await refetch();
              }}
            />
            {/* --- Reference, support and internal tools. Grouped by what the
              reader is trying to do rather than one flat link list. The old
              Integration Assistant CTA that used to live here is now the
              Studio card at the top of the tab. --- */}
            <Card className="sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Docs, Support &amp; Tools
              </h3>

              <ResourceLinkList title="API Reference" links={REFERENCE_LINKS} />
              <ResourceLinkList title="Get Help" links={SUPPORT_LINKS} />

              <div className="mt-5">
                <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Tools
                </p>
                <ul
                  role="list"
                  className="mt-2 divide-y divide-gray-200 rounded-md border border-gray-200"
                >
                  <li className="flex items-center gap-2 py-2.5 px-3 text-sm">
                    <SparklesIcon className="h-4 w-4 flex-shrink-0 text-indigo-500" />
                    <Link
                      to="/changelog"
                      className="font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      What&apos;s New &amp; Changelog
                    </Link>
                  </li>
                  <li className="flex items-center gap-2 py-2.5 px-3 text-sm">
                    <SparklesIcon className="h-4 w-4 flex-shrink-0 text-indigo-500" />
                    <Link
                      to="/tools/mealplans"
                      className="font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      Meal Planning Tool
                    </Link>
                  </li>
                </ul>
              </div>
            </Card>
          </div>

          <div className="flex min-w-0 flex-col gap-6">
            {/* --- Plan, Tokens & Refills --- Single home for everything about
            what this account pays and how tokens are topped up. The plan
            pricing / token budget / refill cost breakdown used to be
            duplicated inside the Current Period card; it lives here now so
            Current Period stays focused on the meter and the projection. */}
            {currentTier && productSubDetail ? (
              <Card className="sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Plan, Tokens &amp; Refills
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      What this plan includes, and how extra tokens are
                      purchased when you run past it.
                    </p>
                  </div>
                  <span className={TIER_PILL_CLASS}>
                    {currentTierDisplayName}
                    {tierSubscriptionPrice > 0
                      ? ` · ${formatUsd(tierSubscriptionPrice)}/mo`
                      : ""}
                  </span>
                </div>

                {/* ----- Plan at a glance ----- */}
                <dl className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {[
                    {
                      label: "Included / period",
                      value: convertToNumLetter(includedAllowanceTokens),
                    },
                    {
                      label: "Refilled so far",
                      value: convertToNumLetter(refillAllowanceTokens),
                    },
                    {
                      label: "Total available",
                      value: convertToNumLetter(totalAllowanceTokens),
                    },
                    {
                      label: "Refill rate",
                      value: `${formatUsd(refillPricePerM)}/1M`,
                    },
                  ].map(({ label, value }) => (
                    <div
                      key={label}
                      className="rounded-lg border border-gray-200 bg-gray-50/70 px-3 py-2"
                    >
                      <dt className="text-sm font-medium uppercase tracking-wide text-gray-500">
                        {label}
                      </dt>
                      <dd className="text-lg font-semibold text-gray-900">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>

                {/* ----- Period-to-date cost. Open by default: the projected total
                is the number customers come to this card for, and hiding the
                subscription / refill split behind a click made it look like
                the card was withholding the answer. ----- */}
                <details
                  open
                  className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800"
                >
                  <summary className="cursor-pointer font-semibold text-slate-700">
                    Cost this period:{" "}
                    {formatUsd(
                      tierSubscriptionPrice + totalProjectedRefillCost
                    )}{" "}
                    projected
                  </summary>
                  <div className="mt-2 space-y-1.5 border-t border-slate-200 pt-2">
                    <div className="flex items-center justify-between gap-3">
                      <span>Subscription fee</span>
                      <span className="font-semibold">
                        {tierSubscriptionPrice > 0
                          ? formatUsd(tierSubscriptionPrice)
                          : "Unavailable"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span>
                        Refill batch ({metadataRefillBatchQuantity}M tokens)
                      </span>
                      <span className="font-semibold">
                        {formatUsd(configuredRefillBatchCost)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span>Refills charged so far</span>
                      <span className="font-semibold">
                        {formatUsd(refillCostSoFar)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 text-amber-700">
                      <span>Additional refills projected</span>
                      <span className="font-semibold">
                        {formatUsd(additionalProjectedRefillCost)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-1.5 font-semibold">
                      <span>Projected period total</span>
                      <span>
                        {formatUsd(
                          tierSubscriptionPrice + totalProjectedRefillCost
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 text-slate-600">
                      <span>
                        Used{" "}
                        {convertToNumLetter(Math.round(includedUsedTokens))}{" "}
                        included +{" "}
                        {convertToNumLetter(Math.round(refillUsedTokens))}{" "}
                        refill
                      </span>
                      <span>
                        {convertToNumLetter(Math.round(currentUsageTokens))} /{" "}
                        {convertToNumLetter(totalAllowanceTokens)}
                      </span>
                    </div>
                  </div>
                </details>

                <div className="mt-5 border-t border-gray-200 pt-5 grid gap-4 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="refillBatchQuantity"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Refill quantity per purchase
                    </label>
                    <div className="flex items-end">
                      <input
                        type="number"
                        id="refillBatchQuantity"
                        value={refillBatchQuantity}
                        onChange={(e) =>
                          setRefillBatchQuantity(
                            Math.max(1, parseInt(e.target.value, 10) || 1)
                          )
                        }
                        className="w-24 px-3 py-2 text-sm rounded-md border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      />
                      <span className="text-sm pl-2 whitespace-nowrap text-gray-600">
                        M tokens
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Immediate refill charge:{" "}
                      <span className="font-semibold text-gray-700">
                        ${immediateRefillCost.toFixed(2)}
                      </span>
                    </p>
                    <button
                      onClick={handleRefillNow}
                      disabled={
                        refillSettingsDirty ||
                        refillNowLoading ||
                        refillSettingsSaving
                      }
                      className="mt-3 px-4 py-2 text-sm rounded-md border border-indigo-700 text-white bg-indigo-700 hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {refillNowLoading ? "Purchasing..." : "Refill Now"}
                    </button>
                  </div>

                  <div>
                    <label
                      htmlFor="refillBehaviour"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Service behavior when balance is low
                    </label>
                    <select
                      id="refillBehaviour"
                      value={refillBehaviour}
                      onChange={(e) =>
                        setRefillBehaviour(e.target.value as "auto" | "off")
                      }
                      className="w-full px-3 py-2 text-sm rounded-md border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    >
                      <option value="auto">
                        Auto-refill and continue service
                      </option>
                      <option value="off">
                        Stop requests when tokens are exhausted
                      </option>
                    </select>
                    <p className="text-sm text-gray-500 mt-1">
                      Auto-refill charges one refill batch at request time.
                    </p>
                  </div>
                </div>

                <div className="mt-5 border-t border-gray-200 pt-4">
                  <p className="block text-sm font-medium text-gray-700 mb-1">
                    Auto pre-purchase on subscription renewal
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Adds tokens at each renewal and charges at your tier refill
                    rate.
                  </p>
                  <div className="mt-2 flex items-end">
                    <input
                      type="number"
                      id="autoRefillOnRenewal"
                      value={autoRefillOnRenewal}
                      onChange={(e) =>
                        setAutoRefillOnRenewal(
                          Math.max(0, parseInt(e.target.value, 10) || 0)
                        )
                      }
                      className="w-24 px-3 py-2 text-sm rounded-md border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                    <span className="text-sm pl-2 whitespace-nowrap text-gray-600">
                      M tokens at each renewal
                    </span>
                  </div>
                </div>

                <div className="mt-5 p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                  <p className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                    At your next renewal
                  </p>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subscription</span>
                    <span className="font-medium text-gray-900">
                      {currentTier.price}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Included tokens</span>
                    <span className="font-medium text-gray-900">
                      {currentTier.tokens}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Auto purchased tokens</span>
                    <span className="font-medium text-gray-900">
                      {autoRefillOnRenewal}M
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Total tokens available
                    </span>
                    <span className="font-medium text-gray-900">
                      {totalTokensAtRenewalM}M
                    </span>
                  </div>
                  {autoRefillOnRenewal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Renewal pre-purchase charge
                      </span>
                      <span className="font-medium text-indigo-600">
                        +${renewalRefillCost.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <hr className="my-1" />
                  <div className="flex justify-between font-semibold">
                    <span className="text-gray-900">
                      Estimated total per month
                    </span>
                    <span className="text-gray-900">
                      $
                      {(
                        currentTier.priceNum + renewalRefillCost
                      ).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>

                {refillSettingsDirty && (
                  <p className="mt-3 text-sm text-amber-600">
                    You have unsaved changes. Save settings before using
                    &nbsp;&quot;Refill Now&quot; to apply your latest refill
                    quantity and renewal amount.
                  </p>
                )}

                {refillSettingsDirty && (
                  <div className="mt-4 flex flex-wrap gap-3 justify-end">
                    <button
                      onClick={saveRefillPreferences}
                      disabled={refillSettingsSaving}
                      className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {refillSettingsSaving
                        ? "Saving..."
                        : "Save Refill Settings"}
                    </button>
                  </div>
                )}
              </Card>
            ) : (
              <Card>
                <Spinner />
              </Card>
            )}

            {/* --- Privacy & Compliance Card (Zero Data Retention) ---
            Visible on every paid AND free tier (so free customers see
            the value prop), but the toggle itself is locked unless the
            account is on Growth or Pro (same gate as the Insights tab via
            `insightsUnlocked`). Free tier sees a lock + upgrade CTA. */}
            {zdrFeatureFlagEnabled && productSubDetail ? (
              <Card
                id="zdr-card"
                className={`sm:p-6 scroll-mt-24 border-l-4 ${
                  !insightsUnlocked
                    ? "border-l-amber-400"
                    : zdrEnabled
                    ? "border-l-emerald-500 bg-emerald-50/30"
                    : "border-l-indigo-400"
                }`}
              >
                <div className="flex items-start gap-4">
                  <span
                    className={`shrink-0 inline-flex items-center justify-center h-12 w-12 rounded-xl ${
                      !insightsUnlocked
                        ? "bg-amber-100 text-amber-700"
                        : zdrEnabled
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-indigo-100 text-indigo-600"
                    }`}
                    aria-hidden
                  >
                    {!insightsUnlocked ? (
                      <LockClosedIcon className="h-7 w-7" />
                    ) : (
                      <ShieldCheckIcon className="h-7 w-7" />
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-xl font-semibold text-gray-900">
                        Privacy &amp; Compliance
                      </h3>
                      {zdrComingSoon ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800 ring-1 ring-inset ring-sky-600/30">
                          Coming soon
                        </span>
                      ) : !insightsUnlocked ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 ring-1 ring-inset ring-amber-600/30">
                          Growth / Pro feature
                        </span>
                      ) : zdrEnabled ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-600/20">
                          ZDR active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600 ring-1 ring-inset ring-gray-400/30">
                          ZDR off
                        </span>
                      )}
                    </div>
                    <p className="text-base text-gray-600 mt-1">
                      Healthcare and regulated workloads can restrict this
                      account to AI models with a no-train / no-retention
                      contract, with an optional US-only data-residency mode.
                    </p>
                  </div>
                </div>

                <div
                  className={`mt-5 border rounded-lg p-5 ${
                    !insightsUnlocked
                      ? "border-amber-200 bg-amber-50/40"
                      : zdrEnabled
                      ? "border-emerald-200 bg-white"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <label
                    htmlFor="zdrToggle"
                    className={`flex items-start gap-4 select-none ${
                      insightsUnlocked && !zdrComingSoon
                        ? "cursor-pointer"
                        : "cursor-not-allowed"
                    }`}
                    title={
                      zdrComingSoon
                        ? "Zero Data Retention is launching soon."
                        : insightsUnlocked
                        ? undefined
                        : "Zero Data Retention is available on Growth and Pro plans."
                    }
                  >
                    <input
                      id="zdrToggle"
                      type="checkbox"
                      className="mt-1 h-5 w-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      checked={insightsUnlocked && !zdrComingSoon && zdrEnabled}
                      disabled={!insightsUnlocked || zdrSaving || zdrComingSoon}
                      onChange={(e) => {
                        if (!insightsUnlocked || zdrComingSoon) return;
                        setZdrConfirmAction(
                          e.target.checked ? "enable" : "disable"
                        );
                      }}
                    />
                    <div className="flex-1">
                      <span className="block text-base font-semibold text-gray-900">
                        Zero Data Retention (No-Train Mode)
                      </span>
                      <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                        Restricts every AI request from this account to models
                        routed through Google Vertex AI under our
                        zero-retention, no-training contract (GCP
                        abuse-monitoring exemption on file).
                      </p>
                      <ul className="text-sm text-gray-700 mt-3 space-y-1.5 list-disc pl-5">
                        <li>
                          Models from OpenAI, xAI direct, Groq, Cerebras, and
                          any other provider without an equivalent contract
                          become unavailable &mdash; including as automatic
                          fallbacks.
                        </li>
                        <li>
                          Per-route overrides pointing to non-eligible models
                          are silently substituted with the closest eligible
                          model at request time.
                        </li>
                        <li>
                          Calls that explicitly request a non-eligible model via{" "}
                          <code className="font-mono text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-900">
                            ?model=
                          </code>{" "}
                          are rejected with{" "}
                          <span className="font-semibold">HTTP 403</span>.
                        </li>
                        <li>
                          Data region is configurable below. By default
                          (&ldquo;Fastest available&rdquo;) eligible models run
                          on the lowest-latency Vertex AI endpoints, which may
                          process data outside the US. Switch to &ldquo;United
                          States only&rdquo; to pin processing to US regions
                          &mdash; the no-training contract applies either way.
                        </li>
                      </ul>
                    </div>
                  </label>
                </div>

                {insightsUnlocked && !zdrComingSoon && zdrEnabled && (
                  <fieldset className="mt-4 border border-gray-200 rounded-lg p-5">
                    <legend className="px-1 text-sm font-semibold text-gray-900">
                      Data region
                      {!zdrRegionControls && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-800 align-middle">
                          Coming soon
                        </span>
                      )}
                    </legend>
                    <p className="text-sm text-gray-600 mt-1">
                      Choose where eligible models run. This does not change the
                      no-training / no-retention contract &mdash; only where
                      requests are processed.
                    </p>
                    {!zdrRegionControls && (
                      <p className="text-xs text-sky-900 bg-sky-50 border border-sky-200 rounded-lg p-3 mt-3 leading-relaxed">
                        Region selection isn&rsquo;t available yet. Eligible
                        models currently run in{" "}
                        <span className="font-semibold">Fastest available</span>{" "}
                        mode for everyone; the &ldquo;United States only&rdquo;
                        residency option will unlock here once it ships.
                      </p>
                    )}
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <label
                        className={`flex items-start gap-3 rounded-lg border p-3 ${
                          zdrRegionSaving || !zdrRegionControls
                            ? "opacity-60 cursor-not-allowed"
                            : "cursor-pointer"
                        } ${
                          effectiveRegionMode === "fastest"
                            ? "border-emerald-400 bg-emerald-50/40 ring-1 ring-emerald-400"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="zdrRegion"
                          className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                          checked={effectiveRegionMode === "fastest"}
                          disabled={zdrRegionSaving || !zdrRegionControls}
                          onChange={() => saveZdrRegion("fastest")}
                        />
                        <span>
                          <span className="block text-sm font-semibold text-gray-900">
                            Fastest available{" "}
                            <span className="font-normal text-gray-500">
                              (default)
                            </span>
                          </span>
                          <span className="block text-xs text-gray-600 mt-1 leading-relaxed">
                            Lowest latency and least likely to be throttled.
                            Uses Vertex AI global endpoints, so data may be
                            processed outside the US.
                          </span>
                        </span>
                      </label>
                      <label
                        className={`flex items-start gap-3 rounded-lg border p-3 ${
                          zdrRegionSaving || !zdrRegionControls
                            ? "opacity-60 cursor-not-allowed"
                            : "cursor-pointer"
                        } ${
                          effectiveRegionMode === "us"
                            ? "border-emerald-400 bg-emerald-50/40 ring-1 ring-emerald-400"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="zdrRegion"
                          className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                          checked={effectiveRegionMode === "us"}
                          disabled={zdrRegionSaving || !zdrRegionControls}
                          onChange={() => saveZdrRegion("us")}
                        />
                        <span>
                          <span className="block text-sm font-semibold text-gray-900">
                            United States only
                          </span>
                          <span className="block text-xs text-gray-600 mt-1 leading-relaxed">
                            Pins processing to US regions for data residency.
                            May be slower or throttled at times when US capacity
                            is constrained.
                          </span>
                        </span>
                      </label>
                    </div>
                  </fieldset>
                )}

                {zdrComingSoon ? (
                  <div className="mt-4 flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-200 p-4 text-sm text-sky-900">
                    <ShieldCheckIcon className="h-5 w-5 shrink-0 mt-0.5" />
                    <p>
                      Zero Data Retention is{" "}
                      <span className="font-semibold">coming soon</span>.
                      We&rsquo;re finalizing rollout &mdash; the toggle will
                      unlock here once it&rsquo;s available for your account.
                      Reach out to{" "}
                      <a
                        href="mailto:support@passiolife.com"
                        className="font-semibold underline"
                      >
                        support@passiolife.com
                      </a>{" "}
                      if you have an urgent compliance requirement.
                    </p>
                  </div>
                ) : !insightsUnlocked ? (
                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4">
                    <div className="flex items-start gap-2 text-sm text-amber-900">
                      <LockClosedIcon className="h-5 w-5 shrink-0 mt-0.5" />
                      <p>
                        Zero Data Retention is available on{" "}
                        <span className="font-semibold">Growth</span> and{" "}
                        <span className="font-semibold">Pro</span> plans.
                        Upgrade to enable strict no-train / US-residency AI for
                        this account.
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        navigate(
                          `/manage-subscription?product=${NUTRITION_PLATFORM_SLUG}`
                        )
                      }
                      className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md bg-amber-600 text-white hover:bg-amber-700"
                    >
                      Upgrade plan
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : zdrEnabled ? (
                  <p className="mt-4 text-sm text-gray-600">
                    Visit the{" "}
                    <span className="font-semibold text-gray-800">
                      Insights
                    </span>{" "}
                    tab to review which per-route model overrides are affected.
                    Non-eligible models are hidden from the model dropdowns
                    while ZDR is on.
                  </p>
                ) : (
                  <p className="mt-4 text-sm text-gray-600">
                    Recommended for healthcare and other regulated customers,
                    and anyone with a Business Associate Agreement (BAA)
                    requirement.
                  </p>
                )}
              </Card>
            ) : null}
          </div>
        </div>

        {/* --- ZDR confirm dialog --- */}
        {zdrFeatureFlagEnabled && zdrConfirmAction !== null && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            role="dialog"
            aria-modal="true"
          >
            <div className="bg-white rounded-lg shadow-xl max-w-md w-[92%] p-6">
              <h4 className="text-lg font-semibold text-gray-900">
                {zdrConfirmAction === "enable"
                  ? "Enable Zero Data Retention?"
                  : "Disable Zero Data Retention?"}
              </h4>
              <p className="text-sm text-gray-600 mt-3">
                {zdrConfirmAction === "enable"
                  ? "This will immediately restrict every AI call on this account to models routed through Vertex AI. Existing integrations that request non-eligible models (for example OpenAI GPT or xAI Grok via the direct provider API) will start receiving a different model or, for explicit ?model= requests, an HTTP 403 error."
                  : "Your account will no longer be restricted to zero-retention models. Future AI calls may be served by providers without our zero-retention contract."}
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => setZdrConfirmAction(null)}
                  disabled={zdrSaving}
                >
                  Cancel
                </button>
                <button
                  className={
                    "px-4 py-2 text-sm rounded-md text-white disabled:opacity-50 " +
                    (zdrConfirmAction === "enable"
                      ? "bg-indigo-600 hover:bg-indigo-700"
                      : "bg-red-600 hover:bg-red-700")
                  }
                  onClick={() =>
                    saveZeroDataRetention(zdrConfirmAction === "enable")
                  }
                  disabled={zdrSaving}
                >
                  {zdrSaving
                    ? "Saving..."
                    : zdrConfirmAction === "enable"
                    ? "Enable ZDR"
                    : "Disable ZDR"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* --- Usage Breakdown --- Replaces the old "Period Usage Detail" and
            "Your Route Spend Benchmarks" cards, which between them took two
            full-width rows to say less than this does. Same data, no extra
            API calls: the grouped period usage query plus the customer route
            averages already fetched above. Deep links into the Insights tab
            for anyone who wants per-route analytics. */}
        <div className="w-full">
          <PlatformUsageBreakdown
            usageDetail={usageDetail}
            groupModes={groupModes}
            groupMode={usageDetailGroupMode}
            onGroupModeChange={setUsageDetailGroupMode}
            detailLoading={usageDetailLoading || usageDetailFetching}
            routeAverages={routeAverages}
            routeAveragesLoading={routeAvgLoading || routeAvgFetching}
            routeWindowDays={GLOBAL_ROUTE_AVG_DAYS}
            refillPricePerM={refillPricePerM}
            refillRateLabel={currentTier?.refillPrice}
            periodLabel={
              currentPeriodStartUnix > 0 && currentPeriodEndUnix > 0
                ? `${new Date(
                    currentPeriodStartUnix * 1000
                  ).toLocaleDateString()} – ${new Date(
                    currentPeriodEndUnix * 1000
                  ).toLocaleDateString()}`
                : undefined
            }
            insightsUnlocked={insightsUnlocked}
            onOpenInsights={() => setActiveTab("insights")}
            onOpenUpgrade={() =>
              navigate(
                `/manage-subscription?product=${NUTRITION_PLATFORM_SLUG}`
              )
            }
          />
        </div>
      </div>

      {/* ===== Affiliate Earnings Tab ===== */}
      {affiliateWorkflowEnabled && (
        <div className={activeTab === "affiliate" ? "block" : "hidden"}>
          {affiliateEarningsUnlocked ? (
            <AffiliateEarnings />
          ) : (
            <Card className="sm:p-6 text-center">
              <p className="text-gray-500 text-lg">
                Affiliate earnings are not enabled for this plan.
              </p>
              <p className="text-gray-400 text-sm mt-2">
                Contact support or upgrade your subscription to unlock this
                view.
              </p>
            </Card>
          )}
        </div>
      )}

      {/* ===== Easy AI Tab ===== */}
      <div className={activeTab === "easyai" ? "block" : "hidden"}>
        <Card className="sm:p-0 overflow-hidden">
          <div className="mx-4 my-4 rounded-lg border border-amber-200 bg-amber-50 overflow-hidden">
            <button
              onClick={() => setShowEasyAISignIn((prev) => !prev)}
              className="w-full p-3 flex items-center justify-between hover:bg-amber-100/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5 text-amber-600" />
                <span className="font-semibold text-amber-900">
                  Easy AI Sign-in Info
                </span>
              </div>
              <span className="text-sm font-semibold text-amber-700">
                {showEasyAISignIn ? "Hide" : "Show"}
              </span>
            </button>
            {showEasyAISignIn && (
              <div className="px-4 pb-4">
                <p className="text-sm text-amber-800 mb-3">
                  Passio SSO runs automatically when you open this tab.
                </p>
                <p className="text-sm text-amber-800 mb-3">
                  Use the same email as your current account:{" "}
                  <span className="font-mono bg-amber-100 px-1 rounded">
                    {easyAIOwnerEmail || "your email"}
                  </span>
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={openEasyAIInNewTab}
                    className="inline-flex items-center rounded-md border border-indigo-300 bg-indigo-100/70 px-3 py-2 text-sm font-semibold text-indigo-800 shadow-sm transition hover:bg-indigo-100"
                  >
                    Open in new tab
                    <ArrowTopRightOnSquareIcon className="ml-1.5 h-4 w-4" />
                  </button>
                  <button
                    onClick={handleActivateEasyAI}
                    disabled={isActivatingEasyAI}
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-md hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {isActivatingEasyAI ? "Sending..." : "Resend Setup Email"}
                  </button>
                </div>
                {easyAIActivationMessage && (
                  <p className="mt-2 text-sm text-indigo-700">
                    {easyAIActivationMessage}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="h-[95vh] min-h-[1080px]">
            <iframe
              src={easyAIIframeUrl}
              className="w-full h-full border-0"
              title="EasyAI"
            />
          </div>
        </Card>
      </div>

      {/* ===== Insights Tab (gated by insightsAccess metadata) ===== */}
      <div className={activeTab === "insights" ? "block" : "hidden"}>
        {insightsUnlocked ? (
          <RouteInsights
            onOpenEasyAI={() => setActiveTab("easyai")}
            onOpenUpgrade={() =>
              navigate(
                `/manage-subscription?product=${NUTRITION_PLATFORM_SLUG}`
              )
            }
            packageMode="platform"
            includedTokensPerPeriod={totalAllowanceTokens}
            baseIncludedTokensPerPeriod={baseIncludedTokens}
            refillBatchQuantity={metadataRefillBatchQuantity}
            tierName={tierName}
            subscriptionFeeUsd={tierSubscriptionPrice}
            pricePerMillionOverride={refillPricePerM}
            authoritativeUsageTokens={Number(
              productSubDetail?.usageInfo?.units ||
                productState?.usageInfo?.units ||
                0
            )}
            authoritativeProjectedPeriodTokens={projectedPeriodUsageTokens}
            modelOverridesUnlocked={modelOverridesUnlocked}
            modelOverridesLockedTooltip={modelOverridesLockedTooltip}
            aiInsightsUnlocked={aiInsightsUnlocked}
            aiInsightsLockedTooltip={aiInsightsLockedTooltip}
            subscriptionStartUnix={Number(
              productSubDetail?.stripeSubscription?.start_date || 0
            )}
            billingPeriodStartUnix={Number(
              productSubDetail?.stripeSubscription?.current_period_start || 0
            )}
            billingPeriodEndUnix={Number(
              productSubDetail?.stripeSubscription?.current_period_end || 0
            )}
            zdrEnabled={zdrFeatureFlagEnabled && zdrEnabled}
            zdrFeatureAvailable={zdrFeatureFlagEnabled}
            onOpenZdrSettings={() => {
              setActiveTab("account");
              // Wait one frame for the tab swap, then scroll the card
              // into view.  scroll-mt-24 on the card gives sticky-header
              // breathing room.
              window.setTimeout(() => {
                document
                  .getElementById("zdr-card")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 50);
            }}
          />
        ) : (
          <Card className="sm:p-6 text-center">
            <p className="text-gray-500 text-lg">
              Insights & Settings are available on Growth and Pro plans.
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Upgrade your subscription to unlock route analytics and usage
              insights.
            </p>
          </Card>
        )}
      </div>

      {/* ===== SDK Chatbot Overlay ===== */}
      {isSdkChatbotOpen && (
        <div className="fixed inset-y-0 right-0 w-[550px] bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 bg-indigo-600 text-white">
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
              <h2 className="text-lg font-semibold">SDK Assistant</h2>
            </div>
            <button
              onClick={() => setIsSdkChatbotOpen(false)}
              className="p-1 hover:bg-indigo-700 rounded-md transition-colors"
              aria-label="Close chat"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <iframe
              src="https://easyai.passiolife.com/embed/chatbot?chatbotId=8a3ca850-ffbc-4296-bd30-7efde9cf096b&apiKey=ZT3ift4W1-d7IoOz_x3MQMng6yZocy-2UAXaz62AtEY&customerTag=easyaitest"
              className="w-full h-full border-0"
              title="SDK Assistant Chatbot"
            />
          </div>
          {/* This overlay is now only reached when the Studio is unreachable,
              so the footer routes to the Easy AI tab (which handles sign-in and
              the setup email) rather than advertising the chatbot builder. */}
          <div className="px-4 py-4 bg-gradient-to-r from-[#f7fee7] to-[#ecfccb] border-t border-gray-200">
            <p className="text-sm font-semibold text-gray-800">
              The EasyAI Studio goes further
            </p>
            <p className="mt-1 text-sm text-gray-600 leading-relaxed">
              It has per-platform SDK knowledge with real class and method
              names, pinnable API reference pages, the v2 to v3 migration, and
              it can scaffold a working integration for you. It needs EasyAI
              sign-in set up on this account.
            </p>
            <button
              type="button"
              onClick={() => {
                setIsSdkChatbotOpen(false);
                setActiveTab("easyai");
              }}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#65a30d] to-[#4d7c0f] px-4 py-2 text-sm font-semibold text-white shadow-md transition-all hover:from-[#4d7c0f] hover:to-[#3f6212]"
            >
              <SparklesIcon className="h-4 w-4" />
              Set up EasyAI access
            </button>
          </div>
        </div>
      )}
      {isSdkChatbotOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={() => setIsSdkChatbotOpen(false)}
        />
      )}
    </>
  );
};

export default DetailsNutritionPlatform;
